import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { WORKFLOW_CASCADE_SOURCES, KYC_RULESETS, PRESALES_ACTIONS, V1_RECIPES, V1_CHECK_TYPE_OPTIONS } from '../types'
import type { BuilderWorkflow, RiskBranch, ScopeData, TriState, WorkflowStep } from '../types'
import type { PresalesTask } from '@/api/types'
import { getGuidance } from '../guidance'
import { getFieldById, getGroupById, getSectionById } from '../schema'
import { readSchemaOverridesSync } from '../schema/overridesCache'
import { showToast } from '@store/uiStore'
import { substituteLegalTextPlaceholders } from './sowLegalText'
import { parseDsl } from '../../dashboards/components/ClientSequenceDiagram/dsl-parser'
import type { FlowDefinition, DiagramStep, MessageStep, SelfStep, NoteStep, GroupStep } from '../../dashboards/components/ClientSequenceDiagram/SequenceDiagram'

export type ExportRow = {
  section: string
  field: string
  value: string
  displaySection?: string
  displayField?: string
  schemaFieldId?: string
  schemaGroupId?: string
  schemaSectionId?: string
  guidance?: string
  decisionSummary?: string
  guidanceSource?: 'hardcoded'
  pending?: boolean
  skipPendingCheck?: boolean
}

const BRAND = {
  primary: '#0069FF',
  light: '#E8F0FF',
  slate: [30, 41, 59] as [number, number, number],
  warning: [180, 83, 9] as [number, number, number],
} as const

/** Return a clickable Jira URL from a value that may already be a full URL or just a ticket key. */
const toJiraUrl = (value: string): string => {
  const trimmed = value.trim()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Strip query params (e.g. ?search_id=...) for a clean browse link
    try { return new URL(trimmed).origin + new URL(trimmed).pathname }
    catch { return trimmed }
  }
  return `https://frankieone.atlassian.net/browse/${trimmed}`
}

const DECISION_VALUES = new Set(['unknown', "don't know", 'dont know', 'tbd', ''])

/**
 * Infer whether an AML sub-screening type is enabled.
 *
 * The form never writes to `.enabled` on pepScreeningConfig, sanctionsScreeningConfig, etc.
 * Instead it uses the top-level `aml.level` to show/hide sections. So if the user has
 * selected a level that includes this screening type, we treat it as enabled even if
 * `.enabled` is still at the default 'unknown'.
 *
 * @param configEnabled  The `.enabled` value from the sub-config (may be 'unknown')
 * @param amlLevel       The top-level AML check level
 * @param requiresEnhanced  Whether this screening type requires 'enhanced' level (vs standard+enhanced)
 */
function inferScreeningEnabled(
  configEnabled: TriState | undefined,
  amlLevel: string,
  requiresEnhanced: boolean,
): { value: string; pending: boolean } {
  // If explicitly set to 'yes' or 'no', respect that
  if (configEnabled === 'yes' || configEnabled === 'no') {
    return {
      value: formatTriStateLabel(configEnabled),
      pending: false,
    }
  }

  // Infer from AML level: standard covers PEP+Sanctions; enhanced covers all
  const levelImpliesEnabled = requiresEnhanced
    ? amlLevel === 'enhanced'
    : amlLevel === 'standard' || amlLevel === 'enhanced'

  if (levelImpliesEnabled) {
    return { value: 'Yes', pending: false }
  }

  // Genuinely unknown
  return { value: formatTriStateLabel('unknown'), pending: true }
}

export const isDecisionRequiredValue = (value: string): boolean => {
  const v = value.trim().toLowerCase()
  if (DECISION_VALUES.has(v)) return true
  return false
}

const normalizeTriState = (value: unknown): TriState => {
  if (value === 'yes' || value === 'no' || value === 'unknown') return value
  if (value === true) return 'yes'
  if (value === false) return 'no'
  return 'unknown'
}

const titleCaseFromSnake = (value: string): string => {
  const cleaned = value.replace(/[_-]+/g, ' ').trim()
  if (!cleaned) return ''
  return cleaned
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// Module-level upsell flag — set by buildExportRows / export functions
// before building rows and cleared after. Avoids threading the flag through
// dozens of pure formatting helpers.
let _isUpsellExport = false

const formatDontKnow = (_value?: unknown): string => _isUpsellExport ? 'Existing Settings' : "Don't know"

const formatYesNoDontKnow = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : value
  if (v === 'yes' || v === true) return 'Yes'
  if (v === 'no' || v === false) return 'No'
  if (v === 'unknown' || v === 'tbd' || v === '' || v === null || v === undefined) return formatDontKnow(value)
  return String(value)
}

const formatVendorLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v) return '—'
  if (v.toLowerCase() === 'frankieone') return 'FrankieOne'
  if (v.toLowerCase() === 'dvs') return 'DVS'
  return titleCaseFromSnake(v)
}

const formatAmlRelationshipLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v || v === 'unknown') return formatDontKnow(value)
  if (v === 'frankieone') return 'FrankieOne'
  if (v === 'direct') return 'Direct with vendor'
  return titleCaseFromSnake(v)
}

const formatAmlMonitoringTypeLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v) return '—'
  if (v === 'one-time') return 'One-time'
  if (v === 'ongoing') return 'Ongoing'
  return titleCaseFromSnake(v)
}

const formatPepLevelsLabel = (levels: unknown): string => {
  if (!Array.isArray(levels) || levels.length === 0) return '—'
  const mapped = levels
    .map((x) => (typeof x === 'string' ? x : ''))
    .filter(Boolean)
    .map((x) => {
      const v = x.trim().toLowerCase()
      if (v === 'pep') return 'PEP'
      if (v === 'rca') return 'RCA'
      return titleCaseFromSnake(v)
    })
  return mapped.join(', ')
}

const formatDocTypeLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v) return ''
  const key = v.toLowerCase()
  if (key === 'passport') return 'Passport'
  if (key === 'drivers_license' || key === 'drivers_licence') return "Driver's Licence"
  if (key === 'medicare_manual') return 'Medicare (manual)'
  if (key === 'medicare') return 'Medicare'
  if (key === 'custom') return 'Custom'
  return titleCaseFromSnake(v)
}

const formatDocTypesList = (values: unknown): string => {
  if (!Array.isArray(values) || values.length === 0) return '—'
  const mapped = values.map((x) => formatDocTypeLabel(x)).filter(Boolean)
  return mapped.length ? mapped.join(', ') : '—'
}

const formatParentChildLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value : ''
  if (!v || v === 'unknown') return formatDontKnow(v)
  if (v === 'parent_only') return 'Parent Only'
  if (v === 'parent_child') return 'Parent + Child'
  if (v === 'standalone') return 'Standalone'
  if (v === 'parent') return 'Parent'
  if (v === 'child') return 'Child'
  return titleCaseFromSnake(v)
}

const formatIdvFrontendLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v || v === 'unknown') return formatDontKnow(v)
  if (v === 'hosted_onesdk') return 'HostedOneSDK'
  if (v === 'embedded_onesdk') return 'EmbeddedOneSDK'
  if (v === 'embedded') return 'Embedded'
  if (v === 'none') return 'None'
  return titleCaseFromSnake(v)
}


const formatAmlCoverageLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v) return '—'
  if (v === 'standard') return 'Standard (PEPs and Sanctions)'
  if (v === 'enhanced') return 'Enhanced (PEPs, Sanctions and Media)'
  if (v === 'na') return 'Not applicable'
  return titleCaseFromSnake(v)
}

const formatRuleChoiceLabel = (value: unknown): string => {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v || v === 'unknown') return formatDontKnow(value)
  if (v === 'default') return 'Default'
  if (v === 'custom') return 'Custom'
  return titleCaseFromSnake(v)
}

const getCascadeDefinition = (scope: ScopeData): { defined: boolean; details: string } => {
  const workflows = Array.isArray(scope.workflowsConfig) ? scope.workflowsConfig : []

  const kycSteps: Array<{ workflowName: string; cascadeSources: unknown }> = []

  workflows.forEach((wf) => {
    if (!wf || typeof wf !== 'object') return
    if ((wf as any).kind === 'complex') return

    const workflowName = typeof (wf as any).name === 'string' && (wf as any).name.trim() ? (wf as any).name.trim() : 'Workflow'
    const steps = Array.isArray((wf as any).steps) ? ((wf as any).steps as WorkflowStep[]) : []
    steps.forEach((s) => {
      if (s && typeof s === 'object' && (s as any).type === 'kyc') {
        kycSteps.push({ workflowName, cascadeSources: (s as any).cascadeSources })
      }
    })
  })

  if (kycSteps.length === 0) {
    return { defined: false, details: 'No KYC step found in Workflow Builder.' }
  }

  const missing = kycSteps.filter((s) => !Array.isArray(s.cascadeSources) || s.cascadeSources.length === 0)
  if (missing.length > 0) {
    const names = Array.from(new Set(missing.map((m) => m.workflowName)))
    return {
      defined: false,
      details: `Missing cascade sources for: ${names.join(', ')}`,
    }
  }

  return { defined: true, details: 'Cascade sources configured for all KYC steps.' }
}

const isEmpty = (v: unknown) => {
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  return false
}

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v)) return v.length ? v.map((x) => formatValue(x)).join(', ') : ''
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

const formatTriStateLabel = (v: unknown): string => {
  const n = normalizeTriState(v)
  if (n === 'yes') return 'Yes'
  if (n === 'no') return 'No'
  return formatDontKnow(v)
}

const pdfLabelFromSchema = (fieldId: string, fallback: string): string => {
  const f = getFieldById(fieldId)
  return f?.pdfLabel || fallback
}

const pdfGuidanceFromSchemaWithOptions = (fieldId: string | undefined): string | undefined => {
  if (!fieldId) return undefined
  const f = getFieldById(fieldId)
  if (!f?.pdfDescription) return undefined
  const labels = optionLabelsForField(f)
  if (labels.length === 0) return f.pdfDescription
  return withOptions(f.pdfDescription, labels)
}

const pdfDecisionSummaryFromSchema = (fieldId: string | undefined): string | undefined => {
  if (!fieldId) return undefined
  return getFieldById(fieldId)?.pdfDecisionSummary
}

const pdfDecisionSummaryFromGroup = (groupId: string | undefined): string | undefined => {
  if (!groupId) return undefined
  return getGroupById(groupId)?.pdfDecisionSummary
}

const pdfDecisionSummaryFromSection = (sectionId: string | undefined): string | undefined => {
  if (!sectionId) return undefined
  return getSectionById(sectionId)?.pdfDecisionSummary
}

const pdfGuidanceFromGroup = (groupId: string | undefined): string | undefined => {
  if (!groupId) return undefined
  const g = getGroupById(groupId)
  return g?.pdfDescription
}

const pdfGuidanceFromSection = (sectionId: string | undefined): string | undefined => {
  if (!sectionId) return undefined
  const s = getSectionById(sectionId)
  return s?.pdfDescription
}

const withOptions = (guidance: string, options: string[]) => {
  const clean = options.map((o) => o.trim()).filter(Boolean)
  if (clean.length === 0) return guidance
  return `${guidance} Options: ${clean.join(', ')}.`
}

const optionLabelsForField = (f: { valueType: string; options?: Array<{ label: string }> }): string[] => {
  if (f.options && f.options.length > 0) {
    return f.options.map((o) => o.label).filter(Boolean)
  }

  if (f.valueType === 'tri_state') {
    return [formatDontKnow(), 'Yes', 'No']
  }

  if (f.valueType === 'boolean') {
    return ['Yes', 'No']
  }

  return []
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

const hexToRgb = (hex: string): [number, number, number] => {
  const cleaned = hex.replace('#', '').trim()
  const full = cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned
  const n = Number.parseInt(full, 16)
  // eslint-disable-next-line no-bitwise
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const getImageDataUrlFromPublicWebp = async (
  publicPath: string,
): Promise<{ dataUrl: string; width: number; height: number } | null> => {
  try {
    const res = await fetch(publicPath)
    if (!res.ok) return null
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)

    const img = new Image()
    img.crossOrigin = 'anonymous'

    const loaded = await new Promise<boolean>((resolve) => {
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
      img.src = objectUrl
    })

    if (!loaded) {
      URL.revokeObjectURL(objectUrl)
      return null
    }

    const width = img.naturalWidth || img.width
    const height = img.naturalHeight || img.height

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      URL.revokeObjectURL(objectUrl)
      return null
    }
    ctx.drawImage(img, 0, 0)

    const dataUrl = canvas.toDataURL('image/png')
    URL.revokeObjectURL(objectUrl)
    return { dataUrl, width, height }
  } catch {
    return null
  }
}

export const buildExportRows = (scope: ScopeData, opts?: { includeNonConfigSections?: boolean }): ExportRow[] => {
  const includeNonConfig = opts?.includeNonConfigSections ?? false
  const isUpsell = scope.generalInfo.isUpsell || false
  _isUpsellExport = isUpsell
  const rows: ExportRow[] = []

  // Global schema overrides (server-side, shared across users). Synchronous
  // read from the in-memory cache — hydrated once on app boot.
  const schemaViewerOverrides = readSchemaOverridesSync()

  const svOverride = (rowType: 'field' | 'group' | 'section', id: string | undefined, prop: string): string | undefined => {
    if (!id) return undefined
    const v = schemaViewerOverrides[`${rowType}:${id}`]?.[prop]
    return v && v.trim().length > 0 ? v : undefined
  }

  const add = (row: ExportRow) => {
    const schemaGuidance =
      svOverride('field', row.schemaFieldId, 'pdfDescription') ??
      pdfGuidanceFromSchemaWithOptions(row.schemaFieldId) ??
      svOverride('group', row.schemaGroupId, 'pdfDescription') ??
      pdfGuidanceFromGroup(row.schemaGroupId) ??
      svOverride('section', row.schemaSectionId, 'pdfDescription') ??
      pdfGuidanceFromSection(row.schemaSectionId)

    const schemaDecisionSummary =
      svOverride('field', row.schemaFieldId, 'pdfDecisionSummary') ??
      pdfDecisionSummaryFromSchema(row.schemaFieldId) ??
      svOverride('group', row.schemaGroupId, 'pdfDecisionSummary') ??
      pdfDecisionSummaryFromGroup(row.schemaGroupId) ??
      svOverride('section', row.schemaSectionId, 'pdfDecisionSummary') ??
      pdfDecisionSummaryFromSection(row.schemaSectionId)

    const hardcodedGuidance = getGuidance(row.section, row.field)

    const guidance = row.guidance ?? schemaGuidance ?? hardcodedGuidance
    const decisionSummary = row.decisionSummary ?? schemaDecisionSummary
    const guidanceSource: ExportRow['guidanceSource'] =
      row.guidance !== undefined || schemaGuidance !== undefined || !hardcodedGuidance ? undefined : 'hardcoded'

    rows.push({ ...row, guidance, decisionSummary, guidanceSource })
  }

  // General
  const gi = scope.generalInfo
  add({
    section: 'General',
    field: 'Customer name',
    displayField: pdfLabelFromSchema('general_customer_name', 'Customer name'),
    schemaFieldId: 'general_customer_name',
    value: scope.customerName || '',
    pending: isEmpty(scope.customerName),
    guidance: undefined,
  })

  if ((scope.customerAbn || '').trim()) {
    add({
      section: 'General',
      field: 'ABN',
      displayField: pdfLabelFromSchema('general_customer_abn', 'ABN'),
      schemaFieldId: 'general_customer_abn',
      value: scope.customerAbn,
      pending: false,
      guidance: undefined,
    })
  }

  // Presales hours excluded from SOW PDF — internal tracking only

  if (isUpsell) {
    add({
      section: 'General',
      field: 'Scope type',
      displayField: 'Scope type',
      value: 'Upsell — Changes Only',
      pending: false,
      guidance: undefined,
    })
  }

  add({
    section: 'General',
    field: 'Forecast checks (12m)',
    displayField: pdfLabelFromSchema('general_forecast_checks_12m', 'Forecast checks (12m)'),
    schemaFieldId: 'general_forecast_checks_12m',
    value: gi.forecastChecks12m != null ? String(gi.forecastChecks12m) : '—',
    pending: false, // Optional field — no forecast checks is valid
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'Parent / Child relationship',
    displayField: pdfLabelFromSchema('general_parent_child', 'Parent / Child relationship'),
    schemaFieldId: 'general_parent_child',
    value: formatParentChildLabel(gi.parentChild),
    pending: gi.parentChild === 'unknown',
    guidance: undefined,
  })

  if (gi.parentAccountName) {
    add({
      section: 'Advanced',
      field: 'Parent account name',
      displayField: pdfLabelFromSchema('general_parent_account_name', 'Parent account name'),
      schemaFieldId: 'general_parent_account_name',
      value: gi.parentAccountName,
      pending: false,
      guidance: undefined,
    })
  }

  if ((gi.parentChild === 'parent_child' || gi.parentChild === 'child') && gi.childAccountName) {
    add({
      section: 'Advanced',
      field: 'Child account name',
      displayField: pdfLabelFromSchema('general_child_account_name', 'Child account name'),
      schemaFieldId: 'general_child_account_name',
      value: gi.childAccountName,
      pending: false,
      guidance: undefined,
    })
  }

  add({
    section: 'General',
    field: 'Production access date',
    displayField: pdfLabelFromSchema('general_production_access_date', 'Production access date'),
    schemaFieldId: 'general_production_access_date',
    value: gi.productionAccessDate,
    pending: isEmpty(gi.productionAccessDate),
    guidance: undefined,
  })

  add({
    section: 'General',
    field: 'Expected go-live date',
    displayField: pdfLabelFromSchema('general_go_live_date', 'Expected go-live date'),
    schemaFieldId: 'general_go_live_date',
    value: gi.goLiveDate ? gi.goLiveDate + (gi.isHardGoLiveDate ? ' (HARD DATE)' : '') : '',
    pending: isEmpty(gi.goLiveDate),
    guidance: undefined,
  })

  add({
    section: 'General',
    field: 'Integration type',
    displayField: pdfLabelFromSchema('general_integration_type', 'Integration type'),
    schemaFieldId: 'general_integration_type',
    value: gi.integrationType === 'unknown' ? formatDontKnow(gi.integrationType) : titleCaseFromSnake(gi.integrationType),
    pending: gi.integrationType === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'General',
    field: 'Platform version',
    displayField: pdfLabelFromSchema('general_platform_version', 'Platform version'),
    schemaFieldId: 'general_platform_version',
    value: gi.platformVersion === 'unknown' ? formatDontKnow(gi.platformVersion) : gi.platformVersion.toUpperCase(),
    pending: gi.platformVersion === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'General',
    field: 'SSO access required',
    displayField: pdfLabelFromSchema('general_sso_access', 'SSO access required'),
    schemaFieldId: 'general_sso_access',
    value: formatYesNoDontKnow(gi.ssoAccess),
    pending: gi.ssoAccess === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'General',
    field: 'AI features',
    displayField: pdfLabelFromSchema('general_ai_features', 'AI features'),
    schemaFieldId: 'general_ai_features',
    value: formatYesNoDontKnow(gi.aiFeatures),
    pending: gi.aiFeatures === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'Reseller agreement',
    displayField: pdfLabelFromSchema('general_reseller_agreement', 'Reseller agreement'),
    schemaFieldId: 'general_reseller_agreement',
    value: formatYesNoDontKnow(gi.resellerAgreement),
    pending: gi.resellerAgreement === 'unknown',
    guidance: undefined,
  })

  if (gi.resellerAgreement === 'yes') {
    add({
      section: 'Advanced',
      field: 'Reseller name',
      displayField: pdfLabelFromSchema('general_reseller_name', 'Reseller name'),
      schemaFieldId: 'general_reseller_name',
      value: gi.resellerName,
      pending: isEmpty(gi.resellerName),
      guidance: undefined,
    })

    const relationshipLabel =
      gi.resellerRelationshipType === 'selling_to_customers'
        ? 'Reseller selling to their customers'
        : gi.resellerRelationshipType === 'customer_of_reseller'
          ? 'Customer of a reseller'
          : formatDontKnow()
    add({
      section: 'Advanced',
      field: 'Reseller relationship',
      displayField: pdfLabelFromSchema('general_reseller_relationship_type', 'Reseller relationship'),
      schemaFieldId: 'general_reseller_relationship_type',
      value: relationshipLabel,
      pending: gi.resellerRelationshipType === 'unknown',
      guidance: undefined,
    })

    add({
      section: 'Advanced',
      field: 'Predefined configuration',
      displayField: pdfLabelFromSchema('general_reseller_predefined_config', 'Predefined configuration'),
      schemaFieldId: 'general_reseller_predefined_config',
      value: formatYesNoDontKnow(gi.resellerPredefinedConfig),
      pending: gi.resellerPredefinedConfig === 'unknown',
      guidance: undefined,
    })
  }

  add({
    section: 'General',
    field: 'Client use case',
    displayField: pdfLabelFromSchema('general_client_use_case', 'Client use case'),
    schemaFieldId: 'general_client_use_case',
    value: gi.clientUseCase,
    pending: isEmpty(gi.clientUseCase),
    guidance: undefined,
  })

  const filledPhases = gi.phases.filter((p) => p.trim())
  if (filledPhases.length === 0) {
    add({
      section: 'General',
      field: 'Phases',
      displayField: pdfLabelFromSchema('general_phases', 'Phases'),
      schemaFieldId: 'general_phases',
      value: '—',
      pending: true,
      guidance: undefined,
    })
  } else {
    filledPhases.forEach((p, i) => {
      const base = pdfLabelFromSchema('general_phase', 'Phase')
      add({
        section: 'General',
        field: `Phase ${i + 1}`,
        displayField: `${base} ${i + 1}`,
        schemaFieldId: 'general_phase',
        value: p,
        pending: false,
      })
    })
  }

  add({
    section: 'Advanced',
    field: 'Testing requirements',
    displayField: pdfLabelFromSchema('general_testing_requirements', 'Testing requirements'),
    schemaFieldId: 'general_testing_requirements',
    value: gi.testDetails,
    pending: false,
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'SOW Variation',
    displayField: pdfLabelFromSchema('general_sow_variation_note', 'SOW Variation'),
    schemaFieldId: 'general_sow_variation_note',
    value: gi.sowVariationNote || '—',
    pending: false, // Optional field — empty is valid
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'Billing Requirements',
    displayField: pdfLabelFromSchema('general_billing_requirements', 'Billing Requirements'),
    schemaFieldId: 'general_billing_requirements',
    value: gi.billingRequirements || '—',
    pending: false, // Optional field — empty is valid
    guidance: undefined,
  })

  add({
    section: 'Custom Work',
    field: 'Custom Works',
    displayField: pdfLabelFromSchema('general_custom_works', 'Custom Works'),
    schemaFieldId: 'general_custom_works',
    value:
      gi.customWorks.length === 0
        ? '—'
        : gi.customWorks
            .map((w, i) => {
              const tickets = w.jiraTickets?.length
                ? `; Jira: ${w.jiraTickets
                    .map((t) => `${t.ticket || '—'} (${t.status})`)
                    .join(', ')}`
                : ''
              const due = w.dueDate ? `; Due: ${w.dueDate}` : ''
              const effort = w.effortDays !== null && w.effortDays !== undefined ? `; Effort: ${w.effortDays}d` : ''
              const status = w.lifecycleStatus ? `; Status: ${w.lifecycleStatus}` : ''
              const pmApproval = w.pmApprovalStatus && w.pmApprovalStatus !== 'not_requested' ? `; PM Approval: ${w.pmApprovalStatus}` : ''
              return `${i + 1}. ${w.name || 'Unnamed'} (${w.category})${status}${due}${effort}${pmApproval}${tickets}\n${w.description || ''}`
            })
            .join('\n\n'),
    pending: false,
    guidance: undefined,
  })

  // Portal Access Users
  if (gi.portalAccessUsers && gi.portalAccessUsers.length > 0) {
    add({
      section: 'General',
      field: 'Portal access users',
      displayField: pdfLabelFromSchema('general_portal_access_users', 'Portal access users'),
      schemaFieldId: 'general_portal_access_users',
      value: gi.portalAccessUsers
        .map((u) => `${u.name}${u.email ? ` (${u.email})` : ''}`)
        .join('\n'),
      pending: false,
      guidance: undefined,
    })
  }

  // KYC
  const kyc = scope.kycConfig
  const isReportingEntity = normalizeTriState(kyc.isReportingEntity)

  // Upsell: if KYC config is untouched, collapse to a single summary row
  if (isUpsell && kyc.kycRequired === 'unknown') {
    add({
      section: 'KYC',
      field: 'KYC configuration',
      displayField: 'KYC configuration',
      value: 'All existing KYC settings apply — no changes',
      pending: false,
      skipPendingCheck: true,
    })
  } else {

  add({
    section: 'KYC',
    field: 'KYC Required',
    displayField: pdfLabelFromSchema('kyc_required', 'KYC Required'),
    schemaFieldId: 'kyc_required',
    value: formatYesNoDontKnow(kyc.kycRequired),
    pending: kyc.kycRequired === 'unknown',
    guidance: undefined,
  })

  // Gate all KYC detail fields — if KYC is explicitly "No", don't export
  // stale data left over from a cloned deal.
  if (kyc.kycRequired !== 'no') {

  add({
    section: 'Advanced',
    field: 'Reporting entity (AML/CTF)',
    displayField: pdfLabelFromSchema('kyc_is_reporting_entity', 'Reporting entity (AML/CTF)'),
    schemaFieldId: 'kyc_is_reporting_entity',
    value: formatTriStateLabel(isReportingEntity),
    pending: isReportingEntity === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'KYC vendor',
    displayField: pdfLabelFromSchema('kyc_vendor', 'KYC vendor'),
    schemaFieldId: 'kyc_vendor',
    value: formatVendorLabel(kyc.vendor),
    pending: isEmpty(kyc.vendor),
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'Compliance framework',
    displayField: pdfLabelFromSchema('kyc_compliance_framework', 'Compliance framework'),
    schemaFieldId: 'kyc_compliance_framework',
    value: kyc.complianceFramework === 'unknown' ? formatDontKnow(kyc.complianceFramework) : titleCaseFromSnake(kyc.complianceFramework),
    pending: kyc.complianceFramework === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'Specific regulatory requirements',
    displayField: pdfLabelFromSchema('kyc_specific_regulatory_requirements', 'Specific regulatory requirements'),
    schemaFieldId: 'kyc_specific_regulatory_requirements',
    value: kyc.specificRegulatoryRequirements?.trim() ? kyc.specificRegulatoryRequirements.trim() : '—',
    pending: false,
  })

  // Minimum data sources / elements per source removed — superseded by rulesets with customRequirements

  add({
    section: 'Advanced',
    field: 'Name matching tolerance',
    displayField: pdfLabelFromSchema('kyc_name_matching_tolerance', 'Name matching tolerance'),
    schemaFieldId: 'kyc_name_matching_tolerance',
    value: kyc.nameMatchingTolerance === 'tbd' ? formatDontKnow(kyc.nameMatchingTolerance) : titleCaseFromSnake(kyc.nameMatchingTolerance),
    pending: kyc.nameMatchingTolerance === 'tbd',
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'DOB matching tolerance',
    displayField: pdfLabelFromSchema('kyc_dob_matching_tolerance', 'DOB matching tolerance'),
    schemaFieldId: 'kyc_dob_matching_tolerance',
    value: kyc.dobMatchingTolerance === 'tbd' ? formatDontKnow(kyc.dobMatchingTolerance) : titleCaseFromSnake(kyc.dobMatchingTolerance),
    pending: kyc.dobMatchingTolerance === 'tbd',
    guidance: undefined,
  })

  add({
    section: 'Advanced',
    field: 'Address matching tolerance',
    displayField: pdfLabelFromSchema('kyc_address_matching_tolerance', 'Address matching tolerance'),
    schemaFieldId: 'kyc_address_matching_tolerance',
    value: kyc.addressMatchingTolerance === 'tbd' ? formatDontKnow(kyc.addressMatchingTolerance) : titleCaseFromSnake(kyc.addressMatchingTolerance),
    pending: kyc.addressMatchingTolerance === 'tbd',
    guidance: undefined,
  })

  if (kyc.passFailCriteria?.trim()) {
    add({
      section: 'Advanced',
      field: 'Pass/fail criteria',
      displayField: pdfLabelFromSchema('kyc_pass_fail_criteria', 'Pass/fail criteria'),
      schemaFieldId: 'kyc_pass_fail_criteria',
      value: kyc.passFailCriteria,
      pending: false,
      guidance: undefined,
    })
  }

  // KYC Rulesets
  if (kyc.rulesets && kyc.rulesets.length > 0) {
    const rulesetLines = kyc.rulesets.map((ruleset, index) => {
      const rulesetDef = KYC_RULESETS.find((r) => r.key === ruleset.rulesetKey)
      const name = ruleset.name || (rulesetDef?.label) || ruleset.rulesetKey
      const lines = [`Ruleset ${index + 1}: ${name}`]

      if (rulesetDef) {
        const req = rulesetDef.requirements
        lines.push(`  Address: ${req.address}, DOB: ${req.dateOfBirth}, Name: ${req.fullName}, Gov ID: ${req.govId}`)
      } else if (ruleset.customRequirements) {
        const req = ruleset.customRequirements
        lines.push(`  Address: ${req.address}, DOB: ${req.dateOfBirth}, Name: ${req.fullName}, Gov ID: ${req.govId}`)
      }

      return lines.join('\n')
    })

    add({
      section: 'KYC',
      field: 'KYC Rulesets',
      displayField: pdfLabelFromSchema('kyc_rulesets', 'KYC Rulesets'),
      schemaFieldId: 'kyc_rulesets',
      value: rulesetLines.join('\n\n'),
      pending: kyc.rulesets.every((r) => !r.rulesetKey || r.rulesetKey === 'unknown'),
      guidance: undefined,
    })
  }

  // AU government sources removed — redundant with Data Sources / workflow cascade configuration

  // Only show credit bureau sources when credit header check is enabled
  {
    const creditHeaderMode = kyc.enableConsentSources
    const creditHeaderEnabled = creditHeaderMode === 'yes'
    if (creditHeaderEnabled) {
      add({
        section: 'KYC',
        field: 'Credit bureau sources',
        displayField: pdfLabelFromSchema('kyc_credit_bureau_sources_summary', 'Credit bureau sources'),
        schemaFieldId: 'kyc_credit_bureau_sources_summary',
        value: [
          kyc.creditBureauSources.equifaxCreditHeader === 'yes'
            ? `Equifax: ${formatTriStateLabel(kyc.creditBureauSources.equifaxCreditHeader)}` +
              (kyc.creditBureauSources.equifaxCreditHeaderVia && kyc.creditBureauSources.equifaxCreditHeaderVia !== 'unknown'
                ? ` (${kyc.creditBureauSources.equifaxCreditHeaderVia === 'frankieone' ? 'Via FrankieOne' : 'Direct'})`
                : '') +
              (kyc.creditBureauSources.equifaxCreditHeaderVia === 'direct' && kyc.creditBureauSources.equifaxCreditHeaderDirectNote
                ? `\n  Direct details: ${kyc.creditBureauSources.equifaxCreditHeaderDirectNote}`
                : '')
            : `Equifax: ${formatTriStateLabel(kyc.creditBureauSources.equifaxCreditHeader)}`,
          kyc.creditBureauSources.experianCreditHeader === 'yes'
            ? `Experian: ${formatTriStateLabel(kyc.creditBureauSources.experianCreditHeader)}` +
              (kyc.creditBureauSources.experianCreditHeaderVia && kyc.creditBureauSources.experianCreditHeaderVia !== 'unknown'
                ? ` (${kyc.creditBureauSources.experianCreditHeaderVia === 'frankieone' ? 'Via FrankieOne' : 'Direct'})`
                : '') +
              (kyc.creditBureauSources.experianCreditHeaderVia === 'direct' && kyc.creditBureauSources.experianCreditHeaderDirectNote
                ? `\n  Direct details: ${kyc.creditBureauSources.experianCreditHeaderDirectNote}`
                : '')
            : `Experian: ${formatTriStateLabel(kyc.creditBureauSources.experianCreditHeader)}`,
        ].join('\n'),
        pending: Object.values(kyc.creditBureauSources).every((x) => normalizeTriState(x) === 'unknown'),
        guidance: 'To access credit bureau sources, the customer must be a reporting entity (or acting on behalf of one). Obligations include: (1) Capture explicit customer consent before conducting credit header checks. (2) Send written notification to customers when credit header checks fail, including the credit reporting body used and an offer of alternative verification. (3) Comply with information retention requirements under the AML/CTF Act. This is not legal advice - consult your legal team for full compliance.',
      })
    }
  }

  if (kyc.vendor === 'other') {
    add({
      section: 'Advanced',
      field: 'Other vendor name',
      displayField: pdfLabelFromSchema('kyc_other_vendor', 'Other vendor name'),
      schemaFieldId: 'kyc_other_vendor',
      value: kyc.otherVendor,
      pending: isEmpty(kyc.otherVendor),
      guidance: undefined,
    })
  }

  add({
    section: 'KYC',
    field: 'Primary customer jurisdictions',
    displayField: pdfLabelFromSchema('kyc_primary_customer_jurisdictions', 'Primary customer jurisdictions'),
    schemaFieldId: 'kyc_primary_customer_jurisdictions',
    value: formatValue(kyc.primaryCustomerJurisdictions),
    pending: !kyc.primaryCustomerJurisdictions?.length,
    guidance: undefined,
  })

  if (kyc.geographicCoverageNotes?.trim()) {
    add({
      section: 'KYC',
      field: 'Geographic coverage notes',
      displayField: pdfLabelFromSchema('kyc_geographic_coverage_notes', 'Geographic coverage notes'),
      schemaFieldId: 'kyc_geographic_coverage_notes',
      value: kyc.geographicCoverageNotes,
      pending: false,
      guidance: undefined,
    })
  }

  add({
    section: 'KYC',
    field: 'International KYC countries',
    displayField: pdfLabelFromSchema('kyc_international_countries', 'International KYC countries'),
    schemaFieldId: 'kyc_international_countries',
    value: formatValue(kyc.internationalCountries),
    pending: !kyc.internationalCountries?.length,
    guidance: undefined,
  })

  // International Document Acceptance (per-country)
  if (kyc.internationalDocumentAcceptance && Object.keys(kyc.internationalDocumentAcceptance).length > 0) {
    const countryDocLines = Object.entries(kyc.internationalDocumentAcceptance)
      .map(([country, docs]) => {
        const accepted = [
          docs.passport ? 'Passport' : null,
          docs.driversLicense ? "Driver's License" : null,
          docs.otherEnabled && docs.otherText ? `Other: ${docs.otherText}` : null,
        ].filter(Boolean)
        return `${country}: ${accepted.length > 0 ? accepted.join(', ') : 'None selected'}`
      })
      .join('\n')

    add({
      section: 'KYC',
      field: 'International document acceptance',
      displayField: pdfLabelFromSchema('kyc_international_document_acceptance', 'International document acceptance'),
      schemaFieldId: 'kyc_international_document_acceptance',
      value: countryDocLines,
      pending: false,
      guidance: undefined,
    })
  }

  add({
    section: 'KYC',
    field: 'Australian document types',
    displayField: pdfLabelFromSchema('kyc_document_types_au', 'Australian document types'),
    schemaFieldId: 'kyc_document_types_au',
    value: formatDocTypesList(kyc.documentTypesAu),
    pending: kyc.primaryCustomerJurisdictions?.includes('Australia') ? kyc.documentTypesAu.length === 0 : false,
    guidance: undefined,
  })

  if (kyc.auOtherDocumentTypesEnabled) {
    add({
      section: 'KYC',
      field: 'Other AU document types',
      displayField: pdfLabelFromSchema('kyc_au_other_document_types', 'Other AU document types'),
      schemaFieldId: 'kyc_au_other_document_types',
      value: kyc.auOtherDocumentTypesText || '—',
      pending: false, // Optional field — enabling "other doc types" without text is valid
      guidance: undefined,
    })
  }

  add({
    section: 'KYC',
    field: 'Enable credit header check',
    displayField: pdfLabelFromSchema('kyc_enable_credit_header_check_mode', 'Enable credit header check'),
    schemaFieldId: 'kyc_enable_credit_header_check_mode',
    value: typeof kyc.enableConsentSources === 'string'
      ? formatYesNoDontKnow(kyc.enableConsentSources)
      : kyc.enableConsentSources === true ? 'Yes' : 'No',
    pending: kyc.enableConsentSources === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'KYC',
    field: 'Foreign passport verification',
    displayField: pdfLabelFromSchema('kyc_foreign_passport_verification', 'Foreign passport verification'),
    schemaFieldId: 'kyc_foreign_passport_verification',
    value: kyc.dvsForeignPassport && kyc.dvsForeignPassport !== 'unknown'
      ? titleCaseFromSnake(kyc.dvsForeignPassport)
      : formatDontKnow(kyc.dvsForeignPassport),
    pending: !kyc.dvsForeignPassport || kyc.dvsForeignPassport === 'unknown',
    guidance: undefined,
  })

  add({
    section: 'KYC',
    field: 'Accept NZ customers',
    displayField: pdfLabelFromSchema('kyc_accept_nz_customers', 'Accept NZ customers'),
    schemaFieldId: 'kyc_accept_nz_customers',
    value: formatTriStateLabel(kyc.acceptNzCustomers),
    pending: normalizeTriState(kyc.acceptNzCustomers) === 'unknown',
    guidance: undefined,
  })

  if (normalizeTriState(kyc.acceptNzCustomers) === 'yes') {
    add({
      section: 'KYC',
      field: 'NZ document types',
      displayField: pdfLabelFromSchema('kyc_document_types_nz', 'NZ document types'),
      schemaFieldId: 'kyc_document_types_nz',
      value: formatValue(kyc.documentTypesNz),
      pending: kyc.documentTypesNz.length === 0,
      guidance: undefined,
    })
  }

  // Data Sources
  if (kyc.selectedDataSources && kyc.selectedDataSources.length > 0) {
    const dataSourcesList = kyc.selectedDataSources
      .map((ds) => {
        const source = WORKFLOW_CASCADE_SOURCES.find((s) => s.id === ds.sourceId)
        const label = source?.label || ds.sourceId
        const viaText = ds.via === 'direct' ? 'Direct connection' : 'via FrankieOne'
        const noteText = ds.via === 'direct' && ds.directNote ? ` - ${ds.directNote}` : ''
        return `${label} (${viaText}${noteText})`
      })
      .join('; ')

    add({
      section: 'KYC',
      field: 'Data sources',
      displayField: pdfLabelFromSchema('kyc_selected_data_sources', 'Data sources'),
      schemaFieldId: 'kyc_selected_data_sources',
      value: dataSourcesList,
      pending: false,
      guidance: undefined,
    })
  }

  // Alternative AU Sources
  {
    const altSrc = kyc.alternativeAuSources
    if (altSrc) {
      const altLines = [
        altSrc.superchoice !== 'unknown' ? `Superchoice: ${formatTriStateLabel(altSrc.superchoice)}${altSrc.superchoice === 'yes' ? ` (${altSrc.superchoiceVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}${altSrc.superchoiceDirectNote ? ` - ${altSrc.superchoiceDirectNote}` : ''}` : null,
        altSrc.equifaxCommercialPublicRecords !== 'unknown' ? `Equifax Commercial/Public Records: ${formatTriStateLabel(altSrc.equifaxCommercialPublicRecords)}${altSrc.equifaxCommercialPublicRecords === 'yes' ? ` (${altSrc.equifaxCommercialPublicRecordsVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
        altSrc.tenancyDatabase !== 'unknown' ? `Tenancy Database: ${formatTriStateLabel(altSrc.tenancyDatabase)}${altSrc.tenancyDatabase === 'yes' ? ` (${altSrc.tenancyDatabaseVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
        altSrc.phoneDirectoryData !== 'unknown' ? `Phone Directory Data: ${formatTriStateLabel(altSrc.phoneDirectoryData)}${altSrc.phoneDirectoryData === 'yes' ? ` (${altSrc.phoneDirectoryDataVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
        altSrc.datazoo !== 'unknown' ? `DataZoo: ${formatTriStateLabel(altSrc.datazoo)}${altSrc.datazoo === 'yes' ? ` (${altSrc.datazooVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
      ].filter(Boolean)

      if (altLines.length > 0) {
        add({
          section: 'KYC',
          field: 'Alternative AU sources',
          displayField: pdfLabelFromSchema('kyc_alternative_au_sources', 'Alternative AU sources'),
          schemaFieldId: 'kyc_alternative_au_sources',
          value: altLines.join('\n'),
          pending: false,
          guidance: undefined,
        })
      }
    }
  }

  // International Data Sources
  {
    const intlSrc = kyc.internationalDataSources
    if (intlSrc) {
      const intlLines = [
        intlSrc.gdc !== 'unknown' ? `GDC/LSEG: ${formatTriStateLabel(intlSrc.gdc)}${intlSrc.gdc === 'yes' ? ` (${intlSrc.gdcVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}${intlSrc.gdcDirectNote ? ` - ${intlSrc.gdcDirectNote}` : ''}` : null,
        intlSrc.centrix !== 'unknown' ? `Centrix NZ: ${formatTriStateLabel(intlSrc.centrix)}${intlSrc.centrix === 'yes' ? ` (${intlSrc.centrixVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
        intlSrc.equifaxNz !== 'unknown' ? `Equifax NZ: ${formatTriStateLabel(intlSrc.equifaxNz)}${intlSrc.equifaxNz === 'yes' ? ` (${intlSrc.equifaxNzVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
        intlSrc.asiaVerify !== 'unknown' ? `AsiaVerify: ${formatTriStateLabel(intlSrc.asiaVerify)}${intlSrc.asiaVerify === 'yes' ? ` (${intlSrc.asiaVerifyVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
        intlSrc.monnai !== 'unknown' ? `Monnai: ${formatTriStateLabel(intlSrc.monnai)}${intlSrc.monnai === 'yes' ? ` (${intlSrc.monnaiVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}` : null,
        intlSrc.otherRegionalProviders !== 'unknown' ? `Other Regional: ${formatTriStateLabel(intlSrc.otherRegionalProviders)}${intlSrc.otherRegionalProviders === 'yes' ? ` (${intlSrc.otherRegionalProvidersVia === 'direct' ? 'Direct' : 'via FrankieOne'})` : ''}${intlSrc.otherRegionalProvidersDirectNote ? ` - ${intlSrc.otherRegionalProvidersDirectNote}` : ''}` : null,
      ].filter(Boolean)

      if (intlLines.length > 0) {
        add({
          section: 'KYC',
          field: 'International data sources',
          displayField: pdfLabelFromSchema('kyc_international_data_sources', 'International data sources'),
          schemaFieldId: 'kyc_international_data_sources',
          value: intlLines.join('\n'),
          pending: false,
          guidance: undefined,
        })
      }
    }
  }

  if (kyc.dataSourceNotes?.trim()) {
    add({
      section: 'KYC',
      field: 'Data source notes',
      displayField: pdfLabelFromSchema('kyc_data_source_notes', 'Data source notes'),
      schemaFieldId: 'kyc_data_source_notes',
      value: kyc.dataSourceNotes,
      pending: false,
      guidance: undefined,
    })
  }

  if (kyc.specialRequirements?.trim()) {
    add({
      section: 'Advanced',
      field: 'Special requirements',
      displayField: pdfLabelFromSchema('kyc_special_requirements', 'Special requirements'),
      schemaFieldId: 'kyc_special_requirements',
      value: kyc.specialRequirements,
      pending: false,
      guidance: undefined,
    })
  }

  add({
    section: 'Advanced',
    field: 'Custom aging enabled',
    displayField: pdfLabelFromSchema('kyc_aging_enabled', 'Custom aging enabled'),
    schemaFieldId: 'kyc_aging_enabled',
    value: kyc.agingEnabled ? 'Yes' : 'No',
    pending: false,
    guidance: undefined,
  })

  if (kyc.agingEnabled) {
    add({
      section: 'Advanced',
      field: 'Aging period',
      displayField: pdfLabelFromSchema('kyc_aging_period_months', 'Aging period'),
      schemaFieldId: 'kyc_aging_period_months',
      value: `${kyc.agingPeriodMonths || 12} months (${Math.round(((kyc.agingPeriodMonths || 12) / 12) * 10) / 10} ${kyc.agingPeriodMonths === 12 ? 'year' : 'years'})`,
      pending: false,
      guidance: undefined,
    })

    if (kyc.agingCustomConfig) {
      add({
        section: 'Advanced',
        field: 'Custom aging configuration',
        displayField: pdfLabelFromSchema('kyc_aging_custom_config', 'Custom aging configuration'),
        schemaFieldId: 'kyc_aging_custom_config',
        value: kyc.agingCustomConfig,
        pending: false,
        guidance: undefined,
      })
    }
  }

  } // end KYC guard (kycRequired !== 'no')
  } // end upsell KYC else

  // AML
  const aml = scope.amlConfig

  // Upsell: if AML config is untouched, collapse to a single summary row
  if (isUpsell && (!aml.level || aml.level === 'unknown')) {
    add({
      section: 'AML',
      field: 'AML configuration',
      displayField: 'AML configuration',
      value: 'All existing AML settings apply — no changes',
      pending: false,
      skipPendingCheck: true,
    })
  } else {

  const amlEnabledLabel = !aml.level ? formatDontKnow(aml.level) : aml.level === 'na' ? 'No' : 'Yes'
  add({
    section: 'AML',
    field: 'AML enabled',
    displayField: pdfLabelFromSchema('aml_level', 'AML enabled'),
    schemaFieldId: 'aml_level',
    value: amlEnabledLabel,
    pending: isEmpty(aml.level),
    guidance: undefined,
  })

  if (aml.level && aml.level !== 'na') {
    add({
      section: 'AML',
      field: 'AML coverage',
      displayField: pdfLabelFromSchema('aml_coverage', 'AML coverage'),
      schemaFieldId: 'aml_coverage',
      value: aml.level === 'enhanced' ? 'Enhanced (PEPs, Sanctions and Media)' : 'Standard (PEPs and Sanctions)',
      pending: false,
    })
  }

  if (aml.level && aml.level !== 'na') {
    add({
      section: 'AML',
      field: 'Relationship',
      displayField: pdfLabelFromSchema('aml_relationship', 'Relationship'),
      schemaFieldId: 'aml_relationship',
      value: formatAmlRelationshipLabel(aml.relationship),
      pending: aml.relationship === 'unknown',
      guidance: undefined,
    })

    const refinitivInfo = 'Refinitiv (World-Check): Premium enterprise-grade dataset. Full PEP & sanctions coverage. Adverse risk signals via Special Interest Categories (SICs) - structured flags (Fraud, Bribery & Corruption, Sanctions-Related, Tax Violations) derived from media, regulatory and enforcement sources. SICs are curated risk tags, not raw article feeds. FrankieOne does not have access to Refinitiv paid media (e.g. AFR, WSJ).'
    const complyInfo = 'ComplyAdvantage: Pragmatic, cost/feature balanced option. Sanctions: UN, OFAC, EU, UK OFSI, AU DFAT + country/thematic lists (updated every 15 min). PEPs: full multi-level universe (International to Local, SOE/SIE) incl. associates & relatives. Adverse media: entity-level hits via AI/ML across open/web sources (excludes paywalled content). Monitoring: daily delta with webhook/email alerts; typically bundled first year, annual thereafter.'
    const allVendorInfo = [refinitivInfo, complyInfo].join('\n\n')
    const vendorSpecificInfo = aml.vendor === 'refinitiv' ? refinitivInfo : aml.vendor === 'comply' ? complyInfo : allVendorInfo

    add({
      section: 'AML',
      field: 'AML vendor',
      displayField: pdfLabelFromSchema('aml_vendor', 'AML vendor'),
      schemaFieldId: 'aml_vendor',
      value: formatVendorLabel(aml.vendor),
      pending: isEmpty(aml.vendor),
      guidance: allVendorInfo,
      decisionSummary: vendorSpecificInfo,
    })

    if (aml.vendor === 'other') {
      add({
        section: 'AML',
        field: 'Other AML vendor',
        displayField: pdfLabelFromSchema('aml_other_vendor', 'Other AML vendor'),
        schemaFieldId: 'aml_other_vendor',
        value: aml.otherVendor,
        pending: isEmpty(aml.otherVendor),
        guidance: undefined,
      })
    }

    add({
      section: 'AML',
      field: 'PEP levels',
      displayField: pdfLabelFromSchema('aml_pep_levels', 'PEP levels'),
      schemaFieldId: 'aml_pep_levels',
      value: formatPepLevelsLabel(aml.pepLevels),
      pending: aml.pepLevels.length === 0,
      guidance: undefined,
    })

    add({
      section: 'AML',
      field: 'Monitoring type',
      displayField: pdfLabelFromSchema('aml_monitoring_type', 'Monitoring type'),
      schemaFieldId: 'aml_monitoring_type',
      value: formatAmlMonitoringTypeLabel(aml.monitoringType),
      pending: isEmpty(aml.monitoringType),
      guidance: undefined,
    })

    if (aml.monitoringType === 'ongoing' && aml.ongoingMonitoringEnrolment) {
      const enrolmentLabels: Record<string, string> = {
        automatic: 'Automatic (all entities)',
        manual_opt_in: 'Manual Opt-in (per entity)',
        unknown: formatDontKnow(),
      }
      add({
        section: 'AML',
        field: 'Monitoring enrolment mode',
        displayField: 'Monitoring enrolment mode',
        schemaFieldId: '',
        value: enrolmentLabels[aml.ongoingMonitoringEnrolment] || titleCaseFromSnake(aml.ongoingMonitoringEnrolment),
        pending: aml.ongoingMonitoringEnrolment === 'unknown',
        guidance: undefined,
      })
    }

    // Name Matching Fuzziness
    if (aml.fuzziness > 0) {
      add({
        section: 'Advanced',
        field: 'Name matching fuzziness',
        displayField: pdfLabelFromSchema('aml_fuzziness', 'Name matching fuzziness'),
        schemaFieldId: 'aml_fuzziness',
        value: String(aml.fuzziness),
        pending: false,
        guidance: undefined,
      })
    }

    // PEP Query Enrichment
    add({
      section: 'Advanced',
      field: 'Send countries for PEP',
      displayField: pdfLabelFromSchema('aml_send_countries_for_pep', 'Send countries for PEP'),
      schemaFieldId: 'aml_send_countries_for_pep',
      value: formatYesNoDontKnow(aml.sendCountriesForPep),
      pending: aml.sendCountriesForPep === undefined || aml.sendCountriesForPep === null,
      guidance: undefined,
    })

    // Batch Processing
    {
      const bp = aml.batchProcessing || {}
      const bpRequired = bp.required || 'unknown'
      const bpLabels: Record<string, string> = { unknown: formatDontKnow(), not_required: 'Not required', required_regularly: 'Required regularly', required_once: 'Required once' }
      add({
        section: 'Advanced',
        field: 'Batch processing required',
        displayField: pdfLabelFromSchema('aml_batch_processing', 'Batch processing required'),
        schemaFieldId: 'aml_batch_processing',
        value: bpLabels[bpRequired] || bpRequired,
        pending: bpRequired === 'unknown',
        guidance: undefined,
      })

      if (bpRequired !== 'not_required' && bpRequired !== 'unknown' && bp.details) {
        add({
          section: 'Advanced',
          field: 'Batch processing details',
          displayField: pdfLabelFromSchema('aml_batch_processing_details', 'Batch processing details'),
          schemaFieldId: 'aml_batch_processing_details',
          value: bp.details,
          pending: false,
          guidance: undefined,
        })
      }
    }

    // Jurisdiction Screening — only include if explicitly set (not rendered on AML tab)
    {
      const js = aml.jurisdictionScreening || {}
      if (js.screenAllJurisdictions && js.screenAllJurisdictions !== 'unknown') {
        add({
          section: 'AML',
          field: 'Screen all jurisdictions',
          displayField: pdfLabelFromSchema('aml_jurisdiction_screening_all', 'Screen all jurisdictions'),
          schemaFieldId: 'aml_jurisdiction_screening_all',
          value: formatTriStateLabel(js.screenAllJurisdictions),
          pending: false,
          guidance: undefined,
        })

        if (js.screenAllJurisdictions === 'no' && js.specificJurisdictions) {
          add({
            section: 'AML',
            field: 'Specific jurisdictions',
            displayField: pdfLabelFromSchema('aml_jurisdiction_screening_specific', 'Specific jurisdictions'),
            schemaFieldId: 'aml_jurisdiction_screening_specific',
            value: js.specificJurisdictions,
            pending: false,
            guidance: undefined,
          })
        }
      }
    }

    // Entity types screened — Individuals and Businesses have form toggles on AML tab
    add({
      section: 'AML',
      field: 'Individuals screening',
      displayField: pdfLabelFromSchema('aml_screening_entity_individuals', 'Individuals screening'),
      schemaFieldId: 'aml_screening_entity_individuals',
      value: formatTriStateLabel(aml.screeningEntityTypes.individuals),
      pending: aml.screeningEntityTypes.individuals === 'unknown',
      guidance: undefined,
    })

    add({
      section: 'AML',
      field: 'Businesses screening',
      displayField: pdfLabelFromSchema('aml_screening_entity_businesses', 'Businesses screening'),
      schemaFieldId: 'aml_screening_entity_businesses',
      value: formatTriStateLabel(aml.screeningEntityTypes.businesses),
      pending: aml.screeningEntityTypes.businesses === 'unknown',
      guidance: undefined,
    })

    // UBOs, Directors & Officers, Auth Reps — not on AML tab, only include if explicitly set
    const kybImpliesEntityScreening =
      scope.kybConfig.enabled === 'yes' && scope.kybConfig.amlEntityScreening === 'yes'

    const inferEntityScreening = (val: TriState): { value: string; pending: boolean } | null => {
      if (val === 'yes' || val === 'no') return { value: formatTriStateLabel(val), pending: false }
      if (kybImpliesEntityScreening) return { value: 'Yes', pending: false }
      return null // Skip from PDF when unknown and not implied
    }

    const ubosScreening = inferEntityScreening(aml.screeningEntityTypes.ubos)
    if (ubosScreening) {
      add({
        section: 'AML',
        field: 'UBOs screening',
        displayField: pdfLabelFromSchema('aml_screening_entity_ubos', 'UBOs screening'),
        schemaFieldId: 'aml_screening_entity_ubos',
        value: ubosScreening.value,
        pending: ubosScreening.pending,
        guidance: undefined,
      })
    }

    const directorsScreening = inferEntityScreening(aml.screeningEntityTypes.directorsAndOfficers)
    if (directorsScreening) {
      add({
        section: 'AML',
        field: 'Directors & officers screening',
        displayField: pdfLabelFromSchema('aml_screening_entity_directors', 'Directors & officers screening'),
        schemaFieldId: 'aml_screening_entity_directors',
        value: directorsScreening.value,
        pending: directorsScreening.pending,
        guidance: undefined,
      })
    }

    const authRepsScreening = inferEntityScreening(aml.screeningEntityTypes.authorizedRepresentatives)
    if (authRepsScreening) {
      add({
        section: 'AML',
        field: 'Authorized representatives screening',
        displayField: pdfLabelFromSchema('aml_screening_entity_auth_reps', 'Authorized representatives screening'),
        schemaFieldId: 'aml_screening_entity_auth_reps',
        value: authRepsScreening.value,
        pending: authRepsScreening.pending,
        guidance: undefined,
      })
    }

    // PEP Screening Configuration
    {
      const pepConfig = aml.pepScreeningConfig || {} as any
      const pepEnabled = inferScreeningEnabled(pepConfig.enabled, aml.level, false)
      add({
        section: 'AML',
        field: 'PEP screening enabled',
        displayField: pdfLabelFromSchema('aml_pep_screening_enabled', 'PEP screening enabled'),
        schemaFieldId: 'aml_pep_screening_enabled',
        value: pepEnabled.value,
        pending: pepEnabled.pending,
        guidance: undefined,
      })

      add({
        section: 'AML',
        field: 'Include family members',
        displayField: pdfLabelFromSchema('aml_pep_include_family_members', 'Include family members'),
        schemaFieldId: 'aml_pep_include_family_members',
        value: formatTriStateLabel(pepConfig.includeFamilyMembers || 'unknown'),
        pending: !pepConfig.includeFamilyMembers || pepConfig.includeFamilyMembers === 'unknown',
        guidance: undefined,
      })

      add({
        section: 'AML',
        field: 'Include historical PEPs',
        displayField: pdfLabelFromSchema('aml_pep_include_historical_peps', 'Include historical PEPs'),
        schemaFieldId: 'aml_pep_include_historical_peps',
        value: formatTriStateLabel(pepConfig.includeHistoricalPeps || 'unknown'),
        pending: !pepConfig.includeHistoricalPeps || pepConfig.includeHistoricalPeps === 'unknown',
        guidance: undefined,
      })
    }

    // Sanctions Screening Config
    {
      const sanctionsEnabled = inferScreeningEnabled(aml.sanctionsScreeningConfig?.enabled, aml.level, false)
      add({
        section: 'AML',
        field: 'Sanctions screening enabled',
        displayField: pdfLabelFromSchema('aml_sanctions_screening_enabled', 'Sanctions screening enabled'),
        schemaFieldId: 'aml_sanctions_screening_enabled',
        value: sanctionsEnabled.value,
        pending: sanctionsEnabled.pending,
        guidance: undefined,
      })

      if (aml.sanctionsScreeningConfig?.listsToInclude && aml.sanctionsScreeningConfig.listsToInclude.length > 0) {
        const listLabels: Record<string, string> = { all_major: 'All Major', ofac: 'OFAC', un: 'UN', eu: 'EU', uk_hmt: 'UK HMT', dfat: 'DFAT', country_specific: 'Country-specific', custom: 'Custom' }
        add({
          section: 'AML',
          field: 'Sanctions lists',
          displayField: pdfLabelFromSchema('aml_sanctions_lists', 'Sanctions lists'),
          schemaFieldId: 'aml_sanctions_lists',
          value: aml.sanctionsScreeningConfig.listsToInclude.map(l => listLabels[l] || titleCaseFromSnake(l)).join(', '),
          pending: false,
          guidance: undefined,
        })
      } else {
        add({
          section: 'AML',
          field: 'Sanctions lists',
          displayField: pdfLabelFromSchema('aml_sanctions_lists', 'Sanctions lists'),
          schemaFieldId: 'aml_sanctions_lists',
          value: 'FrankieOne Standard',
          pending: false,
          guidance: undefined,
        })
      }

      if (aml.sanctionsScreeningConfig?.customLists?.trim()) {
        add({
          section: 'AML',
          field: 'Custom sanctions lists',
          displayField: pdfLabelFromSchema('aml_sanctions_custom_lists', 'Custom sanctions lists'),
          schemaFieldId: 'aml_sanctions_custom_lists',
          value: aml.sanctionsScreeningConfig.customLists,
          pending: false,
          guidance: undefined,
        })
      }

      if (aml.sanctionsScreeningConfig?.complyAdvanceSanctionIds && aml.sanctionsScreeningConfig.complyAdvanceSanctionIds.length > 0) {
        add({
          section: 'AML',
          field: 'ComplyAdvantage sanction IDs',
          displayField: pdfLabelFromSchema('aml_comply_advance_sanction_ids', 'ComplyAdvantage sanction IDs'),
          schemaFieldId: 'aml_comply_advance_sanction_ids',
          value: aml.sanctionsScreeningConfig.complyAdvanceSanctionIds.join(', '),
          pending: false,
          guidance: undefined,
        })
      }

      const matchActionLabels: Record<string, string> = { tbd: 'TBD', automatic_fail: 'Automatic Fail', manual_review_required: 'Manual Review Required', alert_only: 'Alert Only' }
      const matchAction = aml.sanctionsScreeningConfig?.matchAction || 'tbd'
      add({
        section: 'AML',
        field: 'Sanctions match action',
        displayField: pdfLabelFromSchema('aml_sanctions_match_action', 'Sanctions match action'),
        schemaFieldId: 'aml_sanctions_match_action',
        value: matchActionLabels[matchAction] || matchAction,
        pending: matchAction === 'tbd',
        guidance: undefined,
      })
    }

    // Adverse Media
    if (aml.level === 'enhanced') {
      const adverseMediaEnabled = inferScreeningEnabled(aml.adverseMediaScreeningConfig?.enabled, aml.level, true)
      add({
        section: 'AML',
        field: 'Adverse media screening enabled',
        displayField: pdfLabelFromSchema('aml_adverse_media_enabled', 'Adverse media screening enabled'),
        schemaFieldId: 'aml_adverse_media_enabled',
        value: adverseMediaEnabled.value,
        pending: adverseMediaEnabled.pending,
        guidance: undefined,
      })

      if (aml.adverseMediaScreeningConfig?.categories && aml.adverseMediaScreeningConfig.categories.length > 0) {
        const catLabels: Record<string, string> = { financial_crime: 'Financial Crime', violent_crime: 'Violent Crime', cybercrime: 'Cybercrime', regulatory_enforcement: 'Regulatory Enforcement', corruption: 'Corruption', human_rights: 'Human Rights', environmental: 'Environmental', all: 'All' }
        add({
          section: 'AML',
          field: 'Adverse media categories',
          displayField: pdfLabelFromSchema('aml_adverse_media_categories', 'Adverse media categories'),
          schemaFieldId: 'aml_adverse_media_categories',
          value: aml.adverseMediaScreeningConfig.categories.map(c => catLabels[c] || titleCaseFromSnake(c)).join(', '),
          pending: false,
          guidance: undefined,
        })
      } else {
        add({
          section: 'AML',
          field: 'Adverse media categories',
          displayField: pdfLabelFromSchema('aml_adverse_media_categories', 'Adverse media categories'),
          schemaFieldId: 'aml_adverse_media_categories',
          value: 'FrankieOne Standard',
          pending: false,
          guidance: undefined,
        })
      }

      if (aml.adverseMediaScreeningConfig?.customCategories?.trim()) {
        add({
          section: 'AML',
          field: 'Custom adverse media categories',
          displayField: pdfLabelFromSchema('aml_adverse_media_custom_categories', 'Custom adverse media categories'),
          schemaFieldId: 'aml_adverse_media_custom_categories',
          value: aml.adverseMediaScreeningConfig.customCategories,
          pending: false,
          guidance: undefined,
        })
      }

      if (aml.adverseMediaScreeningConfig?.complyAdvanceMediaIds && aml.adverseMediaScreeningConfig.complyAdvanceMediaIds.length > 0) {
        add({
          section: 'AML',
          field: 'ComplyAdvantage media IDs',
          displayField: pdfLabelFromSchema('aml_comply_advance_media_ids', 'ComplyAdvantage media IDs'),
          schemaFieldId: 'aml_comply_advance_media_ids',
          value: aml.adverseMediaScreeningConfig.complyAdvanceMediaIds.join(', '),
          pending: false,
          guidance: undefined,
        })
      }

      const amCategoryLabels: Record<string, string> = { tbd: 'Standard', standard: 'Standard', premium: 'Premium', both: 'Both' }
      const amCategory = aml.adverseMediaScreeningConfig?.category || 'standard'
      add({
        section: 'AML',
        field: 'Adverse media category',
        displayField: pdfLabelFromSchema('aml_adverse_media_category', 'Adverse media category'),
        schemaFieldId: 'aml_adverse_media_category',
        value: amCategoryLabels[amCategory] || amCategory,
        pending: false,
        guidance: undefined,
      })

      const amMatchActionLabels: Record<string, string> = { tbd: 'TBD', manual_review_required: 'Manual Review Required', alert_only: 'Alert Only', risk_score_impact: 'Risk Score Impact' }
      const amMatchAction = aml.adverseMediaScreeningConfig?.matchAction || 'tbd'
      add({
        section: 'AML',
        field: 'Adverse media match action',
        displayField: pdfLabelFromSchema('aml_adverse_media_match_action', 'Adverse media match action'),
        schemaFieldId: 'aml_adverse_media_match_action',
        value: amMatchActionLabels[amMatchAction] || amMatchAction,
        pending: amMatchAction === 'tbd',
        guidance: undefined,
      })
    }

    // Additional Watchlists
    const watchlistsEnabled = inferScreeningEnabled(aml.additionalWatchlistsConfig?.enabled, aml.level, true)
    add({
      section: 'AML',
      field: 'Additional watchlists enabled',
      displayField: pdfLabelFromSchema('aml_additional_watchlists_enabled', 'Additional watchlists enabled'),
      schemaFieldId: 'aml_additional_watchlists_enabled',
      value: watchlistsEnabled.value,
      pending: false,
      skipPendingCheck: true,
      guidance: undefined,
    })

    if (aml.additionalWatchlistsConfig?.complyAdvanceWatchlistIds && aml.additionalWatchlistsConfig.complyAdvanceWatchlistIds.length > 0) {
      add({
        section: 'AML',
        field: 'ComplyAdvantage watchlist IDs',
        displayField: pdfLabelFromSchema('aml_comply_advance_watchlist_ids', 'ComplyAdvantage watchlist IDs'),
        schemaFieldId: 'aml_comply_advance_watchlist_ids',
        value: aml.additionalWatchlistsConfig.complyAdvanceWatchlistIds.join(', '),
        pending: false,
        guidance: undefined,
      })
    }

    if (aml.additionalWatchlistsConfig.types.length > 0) {
      const watchlistLabels: Record<string, string> = { law_enforcement: 'Law enforcement', regulatory_enforcement: 'Regulatory enforcement', disqualified_directors: 'Disqualified directors', insolvency: 'Insolvency', financial_crime: 'Financial crime', country_specific: 'Country-specific', all: 'All' }
      add({
        section: 'AML',
        field: 'Additional watchlists',
        displayField: pdfLabelFromSchema('aml_additional_watchlists_types', 'Additional watchlists'),
        schemaFieldId: 'aml_additional_watchlists_types',
        value: aml.additionalWatchlistsConfig.types.map(t => watchlistLabels[t] || titleCaseFromSnake(t)).join(', '),
        pending: false,
        guidance: undefined,
      })
    } else {
      add({
        section: 'AML',
        field: 'Additional watchlists',
        displayField: pdfLabelFromSchema('aml_additional_watchlists_types', 'Additional watchlists'),
        schemaFieldId: 'aml_additional_watchlists_types',
        value: 'FrankieOne Standard',
        pending: false,
        guidance: undefined,
      })
    }

    // Custom Block List
    if (aml.customBlockList?.trim()) {
      add({
        section: 'Advanced',
        field: 'Custom block list',
        displayField: pdfLabelFromSchema('aml_custom_block_list', 'Custom block list'),
        schemaFieldId: 'aml_custom_block_list',
        value: aml.customBlockList,
        pending: false,
        guidance: undefined,
      })
    }

    // AML Aging
    add({
      section: 'Advanced',
      field: 'Custom aging enabled',
      displayField: pdfLabelFromSchema('aml_aging_enabled', 'Custom aging enabled'),
      schemaFieldId: 'aml_aging_enabled',
      value: aml.agingEnabled ? 'Yes' : 'No',
      pending: false,
      guidance: undefined,
    })

    if (aml.agingEnabled) {
      add({
        section: 'Advanced',
        field: 'Aging period',
        displayField: pdfLabelFromSchema('aml_aging_period_months', 'Aging period'),
        schemaFieldId: 'aml_aging_period_months',
        value: `${aml.agingPeriodMonths || 12} months (${Math.round(((aml.agingPeriodMonths || 12) / 12) * 10) / 10} ${aml.agingPeriodMonths === 12 ? 'year' : 'years'})`,
        pending: false,
        guidance: undefined,
      })

      if (aml.agingCustomConfig) {
        add({
          section: 'Advanced',
          field: 'Custom aging configuration',
          displayField: pdfLabelFromSchema('aml_aging_custom_config', 'Custom aging configuration'),
          schemaFieldId: 'aml_aging_custom_config',
          value: aml.agingCustomConfig,
          pending: false,
          guidance: undefined,
        })
      }
    }
  }
  } // end upsell AML else

  // KYB
  const kyb = scope.kybConfig

  // Upsell: if KYB config is untouched, collapse to a single summary row
  if (isUpsell && kyb.enabled === 'unknown') {
    add({
      section: 'KYB',
      field: 'KYB configuration',
      displayField: 'KYB configuration',
      value: 'All existing KYB settings apply — no changes',
      pending: false,
      skipPendingCheck: true,
    })
  } else {

  add({
    section: 'KYB',
    field: 'KYB enabled',
    displayField: pdfLabelFromSchema('kyb_enabled', 'KYB enabled'),
    schemaFieldId: 'kyb_enabled',
    value: formatTriStateLabel(kyb.enabled),
    pending: kyb.enabled === 'unknown',
    guidance: undefined,
  })

  if (kyb.enabled === 'yes') {
    // AU Checks — always visible on KYB tab (no auChecksEnabled gate)
    {
      const enabledChecks = []
      if (kyb.auChecks.amlStandard) enabledChecks.push('AML Standard')
      if (kyb.auChecks.amlEnhanced) enabledChecks.push('AML Enhanced')
      if (kyb.auChecks.asicInfo) enabledChecks.push('ASIC Info (Current)')
      if (kyb.auChecks.ubo) enabledChecks.push('UBO')
      if (kyb.auChecks.creditReport) enabledChecks.push('Business Credit Report')

      add({
        section: 'KYB',
        field: 'AU check types',
        displayField: 'AU check types',
        value: enabledChecks.length > 0 ? enabledChecks.join(', ') : '—',
        pending: enabledChecks.length === 0,
        guidance: undefined,
      })
    }

    // UBO Consent & Workflow
    add({
      section: 'KYB',
      field: 'UBO consent awareness',
      displayField: pdfLabelFromSchema('kyb_ubo_consent_aware', 'UBO consent awareness'),
      schemaFieldId: 'kyb_ubo_consent_aware',
      value: formatTriStateLabel(kyb.uboConsentAware),
      pending: kyb.uboConsentAware === 'unknown',
      guidance: undefined,
    })

    add({
      section: 'KYB',
      field: 'SMS phone awareness',
      displayField: pdfLabelFromSchema('kyb_ubo_sms_phone_aware', 'SMS phone awareness'),
      schemaFieldId: 'kyb_ubo_sms_phone_aware',
      value: formatTriStateLabel(kyb.uboSmsPhoneAware),
      pending: kyb.uboSmsPhoneAware === 'unknown',
      guidance: undefined,
    })

    if (kyb.uboWorkflowId) {
      const wf = scope.workflowsConfig.find((w) => w.id === kyb.uboWorkflowId)
      add({
        section: 'KYB',
        field: 'UBO workflow',
        displayField: pdfLabelFromSchema('kyb_ubo_workflow', 'UBO workflow'),
        schemaFieldId: 'kyb_ubo_workflow',
        value: wf ? wf.name : kyb.uboWorkflowId,
        pending: false,
        guidance: undefined,
      })
    }

    // AML Business Entity Screening
    add({
      section: 'Advanced',
      field: 'AML entity screening',
      displayField: pdfLabelFromSchema('kyb_aml_entity_screening_enabled', 'AML entity screening'),
      schemaFieldId: 'kyb_aml_entity_screening_enabled',
      value: formatTriStateLabel(kyb.amlEntityScreening),
      pending: kyb.amlEntityScreening === 'unknown',
      guidance: undefined,
    })

    if (kyb.amlEntityScreening === 'yes' && kyb.amlEntityScreeningTypes.length > 0) {
      const typeLabels: Record<string, string> = { sanctions: 'Sanctions', adverse_media: 'Adverse Media' }
      add({
        section: 'Advanced',
        field: 'AML screening types',
        displayField: 'AML screening types',
        value: kyb.amlEntityScreeningTypes.map((t) => typeLabels[t] || t).join(', '),
        pending: false,
        guidance: undefined,
      })
    }

    // Entity Types Percentage Split
    add({
      section: 'Advanced',
      field: 'Entity type split known',
      displayField: 'Entity type split known',
      value: formatTriStateLabel(kyb.entityTypesEnabled),
      pending: kyb.entityTypesEnabled === 'unknown',
      guidance: undefined,
    })

    if (kyb.entityTypesEnabled === 'yes') {
      const splitParts = [
        kyb.entityTypeSplit.soleTradersPercent > 0 ? `Sole Traders: ${kyb.entityTypeSplit.soleTradersPercent}%` : null,
        kyb.entityTypeSplit.ptyLtdPercent > 0 ? `PTY LTD: ${kyb.entityTypeSplit.ptyLtdPercent}%` : null,
        kyb.entityTypeSplit.companyPercent > 0 ? `Company: ${kyb.entityTypeSplit.companyPercent}%` : null,
        kyb.entityTypeSplit.partnershipPercent > 0 ? `Partnership: ${kyb.entityTypeSplit.partnershipPercent}%` : null,
        kyb.entityTypeSplit.trustPercent > 0 ? `Trust: ${kyb.entityTypeSplit.trustPercent}%` : null,
        kyb.entityTypeSplit.otherPercent > 0 ? `Other: ${kyb.entityTypeSplit.otherPercent}%${kyb.otherEntityType ? ` (${kyb.otherEntityType})` : ''}` : null,
      ].filter(Boolean)
      add({
        section: 'Advanced',
        field: 'Entity type split',
        displayField: 'Entity type percentage split',
        value: splitParts.length > 0 ? splitParts.join(', ') : '—',
        pending: splitParts.length === 0,
        guidance: undefined,
      })
    }

    // International KYB
    add({
      section: 'KYB',
      field: 'International KYB',
      displayField: 'International KYB required',
      value: formatTriStateLabel(kyb.internationalEnabled),
      pending: kyb.internationalEnabled === 'unknown',
      guidance: undefined,
    })

    if (kyb.internationalEnabled === 'yes' && kyb.internationalCountryProducts.length > 0) {
      const productLabels: Record<string, string> = { data_retrieval: 'Data Retrieval', registry_documents: 'Registry Documents', ubo_verify: 'UBO Verify' }
      const countryLines = kyb.internationalCountryProducts.map(
        (cp) => `${cp.country}: ${cp.products.length > 0 ? cp.products.map((p) => productLabels[p] || p).join(', ') : 'No products selected'}`
      )
      add({
        section: 'KYB',
        field: 'International country products',
        displayField: 'International country products',
        value: countryLines.join('\n'),
        pending: false,
        guidance: undefined,
      })
    }

    // Trust Analyser
    add({
      section: 'KYB',
      field: 'Trust Analyser enabled',
      displayField: pdfLabelFromSchema('kyb_trust_analyser_enabled', 'Trust Analyser enabled'),
      schemaFieldId: 'kyb_trust_analyser_enabled',
      value: formatTriStateLabel(kyb.trustAnalyserEnabled),
      pending: kyb.trustAnalyserEnabled === 'unknown',
      guidance: undefined,
    })

    if (kyb.trustAnalyserEnabled === 'yes') {
      const trustParts = [
        kyb.trustTypeSplit.unitPercent > 0 ? `Unit Trusts: ${kyb.trustTypeSplit.unitPercent}%` : null,
        kyb.trustTypeSplit.discretionaryPercent > 0 ? `Discretionary: ${kyb.trustTypeSplit.discretionaryPercent}%` : null,
        kyb.trustTypeSplit.smsfPercent > 0 ? `SMSF: ${kyb.trustTypeSplit.smsfPercent}%` : null,
        kyb.trustTypeSplit.otherPercent > 0 ? `Other: ${kyb.trustTypeSplit.otherPercent}%` : null,
      ].filter(Boolean)
      if (trustParts.length > 0) {
        add({
          section: 'KYB',
          field: 'Trust type split',
          displayField: 'Trust type percentage split',
          value: trustParts.join(', '),
          pending: false,
          guidance: undefined,
        })
      }
    }

    // Onboarding Form
    add({
      section: 'KYB',
      field: 'Onboarding form enabled',
      displayField: pdfLabelFromSchema('kyb_onboarding_form_enabled', 'Onboarding form enabled'),
      schemaFieldId: 'kyb_onboarding_form_enabled',
      value: formatTriStateLabel(kyb.onboardingFormEnabled),
      pending: kyb.onboardingFormEnabled === 'unknown',
      guidance: undefined,
    })

    if (kyb.onboardingFormEnabled === 'yes') {
      add({
        section: 'KYB',
        field: 'Branded KYB form',
        displayField: pdfLabelFromSchema('kyb_onboarding_form_branded', 'Branded KYB form'),
        schemaFieldId: 'kyb_onboarding_form_branded',
        value: formatTriStateLabel(kyb.onboardingFormBranded),
        pending: kyb.onboardingFormBranded === 'unknown',
        guidance: undefined,
      })
    }

    // KYB AML Ongoing Monitoring
    const amlLevel = scope.amlConfig.level
    const isEnhanced = amlLevel === 'enhanced'
    const kybAmlCoverage = isEnhanced
      ? 'Sanctions and adverse media checks on business entities (Enhanced AML). PEPs are not applicable to businesses.'
      : 'Sanctions-only checks on business entities (Standard AML). PEPs are not applicable to businesses.'
    const kybAmlGuidance = amlLevel && amlLevel !== 'na'
      ? kybAmlCoverage
      : 'Ongoing AML monitoring for business entities. Coverage depends on the configured AML level (Standard = sanctions only, Enhanced = sanctions + adverse media). PEPs are not applicable to businesses.'
    add({
      section: 'KYB',
      field: 'KYB AML ongoing monitoring',
      displayField: pdfLabelFromSchema('kyb_aml_ongoing_monitoring_enabled', 'KYB AML ongoing monitoring enabled'),
      schemaFieldId: 'kyb_aml_ongoing_monitoring_enabled',
      value: formatTriStateLabel(kyb.kybAmlOngoingMonitoringEnabled),
      pending: kyb.kybAmlOngoingMonitoringEnabled === 'unknown',
      guidance: kybAmlGuidance,
      decisionSummary: kybAmlGuidance,
    })

    if (kyb.kybAmlOngoingMonitoringEnabled === 'yes' && (kyb.kybAmlOngoingMonitoringNotes || '').trim()) {
      add({
        section: 'KYB',
        field: 'KYB AML ongoing monitoring notes',
        displayField: pdfLabelFromSchema('kyb_aml_ongoing_monitoring_notes', 'KYB AML ongoing monitoring notes'),
        schemaFieldId: 'kyb_aml_ongoing_monitoring_notes',
        value: kyb.kybAmlOngoingMonitoringNotes,
        pending: false,
        guidance: undefined,
      })
    }

    // Continuous KYB Monitoring
    const continuousKybInfo = 'Continuous KYB monitoring via CreditorWatch - ongoing business health, credit risk and compliance signals. Covers ASIC deregistration, credit defaults, court actions, insolvency events and more.'
    add({
      section: 'KYB',
      field: 'Continuous KYB monitoring',
      displayField: pdfLabelFromSchema('kyb_continuous_monitoring_enabled', 'Continuous KYB enabled'),
      schemaFieldId: 'kyb_continuous_monitoring_enabled',
      value: formatTriStateLabel(kyb.continuousKybEnabled),
      pending: kyb.continuousKybEnabled === 'unknown',
      guidance: continuousKybInfo,
      decisionSummary: continuousKybInfo,
    })

    if (kyb.continuousKybEnabled === 'yes' && (kyb.continuousKybNotes || '').trim()) {
      add({
        section: 'KYB',
        field: 'Continuous KYB notes',
        displayField: pdfLabelFromSchema('kyb_continuous_monitoring_notes', 'Continuous KYB notes'),
        schemaFieldId: 'kyb_continuous_monitoring_notes',
        value: kyb.continuousKybNotes,
        pending: false,
        guidance: undefined,
      })
    }

    // Aging
    add({
      section: 'Advanced',
      field: 'Custom aging enabled',
      displayField: pdfLabelFromSchema('kyb_aging_enabled', 'Custom aging enabled'),
      schemaFieldId: 'kyb_aging_enabled',
      value: kyb.agingEnabled ? 'Yes' : 'No',
      pending: false,
      guidance: undefined,
    })

    if (kyb.agingEnabled) {
      add({
        section: 'Advanced',
        field: 'Aging period',
        displayField: pdfLabelFromSchema('kyb_aging_period_months', 'Aging period'),
        schemaFieldId: 'kyb_aging_period_months',
        value: `${kyb.agingPeriodMonths} months (${Math.round((kyb.agingPeriodMonths / 12) * 10) / 10} ${kyb.agingPeriodMonths === 12 ? 'year' : 'years'})`,
        pending: false,
        guidance: undefined,
      })

      if (kyb.agingCustomConfig.trim()) {
        add({
          section: 'Advanced',
          field: 'Custom aging config',
          displayField: pdfLabelFromSchema('kyb_aging_custom_config', 'Custom aging config'),
          schemaFieldId: 'kyb_aging_custom_config',
          value: kyb.agingCustomConfig,
          pending: false,
          guidance: undefined,
        })
      }
    }
  }
  } // end upsell KYB else

  // ID Verification (Biometrics)
  const bio = scope.biometricsConfig

  // Upsell: if IDV config is untouched, collapse to a single summary row
  if (isUpsell && bio.enabled === 'unknown') {
    add({
      section: 'ID Verification',
      field: 'ID Verification configuration',
      displayField: 'ID Verification configuration',
      value: 'All existing ID Verification settings apply — no changes',
      pending: false,
      skipPendingCheck: true,
    })
  } else {

  add({
    section: 'ID Verification',
    field: 'Enabled',
    displayField: pdfLabelFromSchema('biometrics_enabled', 'Enabled'),
    schemaFieldId: 'biometrics_enabled',
    value: formatYesNoDontKnow(bio.enabled),
    pending: bio.enabled === 'unknown',
    guidance: undefined,
  })

  // Gate all Biometrics detail fields — if explicitly "No", don't export
  // stale data left over from a cloned deal.
  if (bio.enabled !== 'no') {

  add({
    section: 'ID Verification',
    field: 'Flows',
    displayField: pdfLabelFromSchema('biometrics_flows_summary', 'Flows'),
    schemaFieldId: 'biometrics_flows_summary',
    value:
      Array.isArray(bio.flows) && bio.flows.length
        ? bio.flows
            .map((f, i) => {
              const steps = [
                f.consentScreenEnabled === 'yes' ? 'Consent' : null,
                f.idFormEnabled === 'yes' ? 'ID Form' : null,
                f.idVerificationEnabled === 'yes' ? 'IDV' : null,
                f.biometricsEnabled === 'yes' ? 'Biometrics' : null,
                f.reviewScreenEnabled === 'yes' ? 'Review' : null,
                f.redirectPageEnabled === 'yes' ? 'Redirect' : null,
                f.customScreenEnabled === 'yes' ? `Custom${f.customScreenDetails ? ` (${f.customScreenDetails})` : ''}` : null,
              ]
                .filter(Boolean)
                .join(' > ')

              const isDefault = Boolean(f.isDefault)
              const label = `${i + 1}) ${f.name || `Flow ${i + 1}`}${isDefault ? ' [DEFAULT]' : ''} (${f.id || '—'})`
              const docsLabel = f.documentTypes.includes('custom') && f.customDocumentTypeDetails ? `Custom: ${f.customDocumentTypeDetails}` : ''
              const docs = [formatDocTypesList(f.documentTypes.filter((d) => d !== 'custom')), docsLabel].filter(Boolean).join(', ') || formatDocTypesList(f.documentTypes)
              const vendor = formatVendorLabel(f.vendor || scope.biometricsConfig.vendor)
              const notes = f.notes || ''

              const lines: string[] = []
              lines.push(label)
              lines.push(`Steps: ${steps || '—'}`)
              lines.push(`Vendor: ${vendor}`)
              if (f.vendor === 'daon' && f.doubleIdEnabled && f.doubleIdEnabled !== 'unknown') {
                lines.push(`Double ID: ${f.doubleIdEnabled === 'yes' ? 'Yes' : 'No'}`)
              }
              lines.push(`Docs: ${docs}`)
              if (notes) lines.push(`Notes: ${notes}`)
              return lines.join('\n')
            })
            .join('\n\n')
        : '—',
    pending:
      bio.enabled === 'yes'
        ? !Array.isArray(bio.flows) || bio.flows.length === 0 || bio.flows.some((f) => !f.id || !f.name)
        : false,
    guidance: undefined,
  })

  {
    // Document types can be set globally (bio.documentTypes) or per-flow (flow.documentTypes).
    // Show whichever is populated — prefer per-flow if flows exist.
    const flowDocTypes = Array.isArray(bio.flows)
      ? [...new Set(bio.flows.flatMap((f) => Array.isArray(f.documentTypes) ? f.documentTypes.filter((d) => d !== 'unknown') : []))]
      : []
    const globalDocTypes = Array.isArray(bio.documentTypes) ? bio.documentTypes.filter((d) => d !== 'unknown') : []
    const effectiveDocTypes = flowDocTypes.length > 0 ? flowDocTypes : globalDocTypes

    add({
      section: 'ID Verification',
      field: 'Supported document types',
      displayField: pdfLabelFromSchema('biometrics_document_types', 'Supported document types'),
      schemaFieldId: 'biometrics_document_types',
      value: effectiveDocTypes.length > 0 ? formatDocTypesList(effectiveDocTypes) : '—',
      pending:
        bio.enabled === 'yes' ? effectiveDocTypes.length === 0 : false,
      guidance: undefined,
    })
  }

  add({
    section: 'Advanced',
    field: 'Platform',
    displayField: pdfLabelFromSchema('biometrics_platform', 'Platform'),
    schemaFieldId: 'biometrics_platform',
    value: bio.platform === 'unknown' ? formatDontKnow(bio.platform) : titleCaseFromSnake(bio.platform || 'unknown'),
    pending: bio.enabled === 'yes' ? isEmpty(bio.platform) || bio.platform === 'unknown' : false,
    guidance: undefined,
  })

  add({
    section: 'ID Verification',
    field: 'Vendor',
    displayField: pdfLabelFromSchema('biometrics_vendor_primary', 'Vendor'),
    schemaFieldId: 'biometrics_vendor_primary',
    value: formatVendorLabel(bio.vendor),
    pending: bio.enabled === 'yes' ? isEmpty(bio.vendor) || bio.vendor === 'unknown' : false,
    guidance: undefined,
  })

  if (bio.vendor === 'other' && bio.otherVendor) {
    add({
      section: 'ID Verification',
      field: 'Other vendor name',
      displayField: pdfLabelFromSchema('biometrics_other_vendor', 'Other vendor name'),
      schemaFieldId: 'biometrics_other_vendor',
      value: bio.otherVendor,
      pending: false,
      guidance: undefined,
    })
  }

  // Add vendor note field (only for non-FrankieOne vendors)
  if (bio.enabled === 'yes' && bio.vendor && bio.vendor !== 'frankieone' && bio.vendor !== 'unknown' && bio.vendorNote) {
    add({
      section: 'ID Verification',
      field: 'Vendor details',
      displayField: pdfLabelFromSchema('biometrics_vendor_note', 'Vendor details'),
      schemaFieldId: 'biometrics_vendor_note',
      value: bio.vendorNote || '—',
      pending: false,
      guidance: undefined,
    })
  }

  add({
    section: 'ID Verification',
    field: 'Custom SMS message',
    displayField: pdfLabelFromSchema('biometrics_sms_template', 'Custom SMS message'),
    schemaFieldId: 'biometrics_sms_template',
    value: bio.customSmsNotRequired ? 'Not required — using default template' : (bio.customSmsMessageTemplate || '—'),
    pending: bio.enabled === 'yes' ? (!bio.customSmsNotRequired && isEmpty(bio.customSmsMessageTemplate)) : false,
    guidance: undefined,
  })

  add({
    section: 'ID Verification',
    field: 'SMS link validity (hours)',
    displayField: pdfLabelFromSchema('biometrics_sms_link_validity_hours', 'SMS link validity (hours)'),
    schemaFieldId: 'biometrics_sms_link_validity_hours',
    value: bio.smsLinkValidityHours === null ? '—' : String(bio.smsLinkValidityHours),
    pending: false,
    guidance: undefined,
  })

  // Branding
  add({
    section: 'ID Verification',
    field: 'Rebranding Hosted OneSDK',
    displayField: pdfLabelFromSchema('biometrics_rebranding_hosted_onesdk', 'Rebranding Hosted OneSDK'),
    schemaFieldId: 'biometrics_rebranding_hosted_onesdk',
    value: formatTriStateLabel(bio.rebrandingHostedOneSDK),
    pending: bio.enabled === 'yes' ? bio.rebrandingHostedOneSDK === 'unknown' : false,
    guidance: undefined,
  })

  add({
    section: 'ID Verification',
    field: 'Rebranding Daon',
    displayField: pdfLabelFromSchema('biometrics_rebranding_daon', 'Rebranding Daon'),
    schemaFieldId: 'biometrics_rebranding_daon',
    value: formatTriStateLabel(bio.rebrandingDaon),
    pending: bio.enabled === 'yes' ? bio.rebrandingDaon === 'unknown' : false,
    guidance: undefined,
  })

  add({
    section: 'ID Verification',
    field: 'Extended branding',
    displayField: pdfLabelFromSchema('biometrics_extended_branding', 'Extended branding'),
    schemaFieldId: 'biometrics_extended_branding',
    value: formatTriStateLabel(bio.extendedBranding),
    pending: bio.enabled === 'yes' ? bio.extendedBranding === 'unknown' : false,
    guidance: undefined,
  })

  add({
    section: 'ID Verification',
    field: 'Custom redirect page',
    displayField: pdfLabelFromSchema('biometrics_custom_redirect_page', 'Custom redirect page'),
    schemaFieldId: 'biometrics_custom_redirect_page',
    value: formatTriStateLabel(bio.customRedirectPage),
    pending: bio.enabled === 'yes' ? bio.customRedirectPage === 'unknown' : false,
    guidance: undefined,
  })

  add({
    section: 'ID Verification',
    field: 'Custom consents',
    displayField: pdfLabelFromSchema('biometrics_custom_consents', 'Custom consents'),
    schemaFieldId: 'biometrics_custom_consents',
    value: formatTriStateLabel(bio.customConsents),
    pending: bio.enabled === 'yes' ? bio.customConsents === 'unknown' : false,
    guidance: undefined,
  })

  if (bio.brandingNotes?.trim()) {
    add({
      section: 'ID Verification',
      field: 'Branding notes',
      displayField: pdfLabelFromSchema('biometrics_branding_notes', 'Branding notes'),
      schemaFieldId: 'biometrics_branding_notes',
      value: bio.brandingNotes,
      pending: false,
      guidance: undefined,
    })
  }

  } // end Biometrics guard (bio.enabled !== 'no')
  } // end upsell IDV else

  // Recipes/Workflows
  const workflowsUntouched = scope.workflowsConfig.length === 0 ||
    scope.workflowsConfig.every((w) => !w.name && (w.kind === 'complex' || w.steps.length === 0))
  if (isUpsell && workflowsUntouched) {
    add({
      section: 'Recipes/Workflows',
      field: 'Workflows configuration',
      displayField: 'Workflows configuration',
      value: 'All existing Workflow settings apply — no changes',
      pending: false,
      skipPendingCheck: true,
    })
  } else {

  add({
    section: 'Recipes/Workflows',
    field: 'About workflows',
    value: svOverride('group', 'recipes_tab_header', 'pdfDescription') ?? pdfGuidanceFromGroup('recipes_tab_header') ?? '—',
    pending: false,
    schemaGroupId: 'recipes_tab_header',
  })

  if (scope.workflowsRequiredCount != null) {
    add({
      section: 'Recipes/Workflows',
      field: 'Workflows required',
      displayField: 'Workflows required',
      value: String(scope.workflowsRequiredCount),
      pending: false,
    })
  }

  if (scope.workflowsNotes?.trim()) {
    add({
      section: 'Recipes/Workflows',
      field: 'Workflow notes',
      displayField: 'Workflow notes',
      value: scope.workflowsNotes,
      pending: false,
    })
  }

  scope.workflowsConfig.forEach((w, idx) => {
    const formatIdvStepDetails = (step: Extract<WorkflowStep, { type: 'idv_biometrics' }>): string => {
      const bio = scope.biometricsConfig
      const selectedFlow = Array.isArray(bio.flows) ? bio.flows.find((f) => f.id === step.flowId) : undefined

      const flowSteps = selectedFlow
        ? [
            selectedFlow.consentScreenEnabled === 'yes' ? 'Consent' : null,
            selectedFlow.idFormEnabled === 'yes' ? 'ID Form' : null,
            selectedFlow.idVerificationEnabled === 'yes' ? 'IDV' : null,
            selectedFlow.biometricsEnabled === 'yes' ? 'Biometrics' : null,
            selectedFlow.reviewScreenEnabled === 'yes' ? 'Review' : null,
            selectedFlow.redirectPageEnabled === 'yes' ? 'Redirect' : null,
            selectedFlow.customScreenEnabled === 'yes' ? `Custom${selectedFlow.customScreenDetails ? ` (${selectedFlow.customScreenDetails})` : ''}` : null,
          ]
            .filter(Boolean)
            .join(' > ')
        : ''

      const docs =
        selectedFlow &&
        Array.isArray(selectedFlow.documentTypes) &&
        selectedFlow.documentTypes.length &&
        !selectedFlow.documentTypes.includes('unknown')
          ? formatDocTypesList(selectedFlow.documentTypes)
          : Array.isArray(bio.documentTypes) && bio.documentTypes.length
            ? formatDocTypesList(bio.documentTypes)
            : '—'

      const lines: string[] = []
      lines.push(`Requirement: ${step.requirement === 'optional' ? 'Optional' : 'Mandatory'}`)
      lines.push(`Enabled: ${formatYesNoDontKnow(bio.enabled)}`)
      lines.push(`Frontend: ${formatIdvFrontendLabel(bio.frontend)}`)
      lines.push(`Vendor: ${formatVendorLabel(bio.vendor)}`)
      lines.push(`Custom SMS message: ${bio.customSmsNotRequired ? 'Not required' : bio.customSmsMessageTemplate ? 'Yes' : 'No'}`)
      if (bio.smsLinkValidityHours !== null) lines.push(`SMS link validity (hours): ${bio.smsLinkValidityHours}`)

      if (selectedFlow) {
        lines.push(`Flow ID: ${selectedFlow.id || '—'}`)
        lines.push(`Flow name: ${selectedFlow.name || '—'}`)
        lines.push(`Flow vendor: ${formatVendorLabel(selectedFlow.vendor)}`)
        if (selectedFlow.vendor === 'daon' && selectedFlow.doubleIdEnabled && selectedFlow.doubleIdEnabled !== 'unknown') {
          lines.push(`Double ID: ${selectedFlow.doubleIdEnabled === 'yes' ? 'Yes' : 'No'}`)
        }
        lines.push(`Flow steps: ${flowSteps || '—'}`)
        lines.push(`Flow documents: ${docs}`)
        if (selectedFlow.notes) lines.push(`Flow notes: ${selectedFlow.notes}`)
      } else {
        lines.push(`Flow ID: ${step.flowId || '—'}`)
        lines.push(`Documents: ${docs}`)
      }

      return lines.join('\n')
    }

    const formatAmlStepDetails = (): string => {
      const aml = scope.amlConfig
      const entityTypes = aml.screeningEntityTypes
      const lines: string[] = []
      lines.push(`Coverage: ${formatAmlCoverageLabel(aml.level)}`)
      lines.push(`Vendor: ${formatVendorLabel(aml.vendor)}`)
      if (aml.vendor === 'other') lines.push(`Other vendor: ${aml.otherVendor || '—'}`)
      lines.push(`PEP levels: ${formatPepLevelsLabel(aml.pepLevels)}`)
      lines.push(`Monitoring type: ${formatAmlMonitoringTypeLabel(aml.monitoringType)}`)
      if (aml.monitoringType === 'ongoing' && aml.ongoingMonitoringEnrolment) {
        const enrolLabel = aml.ongoingMonitoringEnrolment === 'automatic' ? 'Automatic' : aml.ongoingMonitoringEnrolment === 'manual_opt_in' ? 'Manual Opt-in' : 'Unknown'
        lines.push(`Monitoring enrolment: ${enrolLabel}`)
      }
      lines.push(
        `Entity types: Individuals=${formatTriStateLabel(entityTypes.individuals)}, Businesses=${formatTriStateLabel(entityTypes.businesses)}`
      )
      lines.push(`Send countries for PEP: ${formatYesNoDontKnow(aml.sendCountriesForPep)}`)
      if (aml.fuzziness > 0) lines.push(`Name matching fuzziness: ${aml.fuzziness}`)
      const sanctionsAction = aml.sanctionsScreeningConfig?.matchAction || 'tbd'
      if (sanctionsAction !== 'tbd') lines.push(`Sanctions match action: ${titleCaseFromSnake(sanctionsAction)}`)
      if (aml.level === 'enhanced') {
        const amCategory = aml.adverseMediaScreeningConfig?.category || 'tbd'
        if (amCategory !== 'tbd') lines.push(`Adverse media category: ${titleCaseFromSnake(amCategory)}`)
      }
      if (aml.customBlockList?.trim()) lines.push('Custom block list: Yes')
      return lines.join('\n')
    }

    const formatKycStepDetails = (step: Extract<WorkflowStep, { type: 'kyc' }>): string => {
      const configuredRuleset = scope.kycConfig.rulesets.find((r) => r.rulesetKey === step.rulesetId)
      const rulesetDef = KYC_RULESETS.find((r) => r.key === step.rulesetId)
      const rulesetLabel = configuredRuleset?.name || rulesetDef?.label || step.rulesetId || '—'

      const cascadeLabels = step.cascadeSources
        .map((srcId) => WORKFLOW_CASCADE_SOURCES.find((src) => src.id === srcId)?.label || srcId)

      const lines: string[] = []
      lines.push(`Ruleset: ${rulesetLabel}`)
      lines.push(
        `Cascade sources (in order): ${cascadeLabels.length ? cascadeLabels.map((l, i) => `${i + 1}. ${l}`).join('; ') : '—'}`,
      )
      return lines.join('\n')
    }

    const formatWorkflowSteps = (steps: WorkflowStep[], indent: string): string => {
      return steps
        .map((s: WorkflowStep, si: number) => {
          const stepLabel = (type: string) => {
            if (type === 'aml') return 'AML'
            if (type === 'kyc') return 'KYC'
            if (type === 'gov_id') return 'Gov ID'
            if (type === 'blocklist') return 'Blocklist'
            if (type === 'fraud') return 'Fraud'
            if (type === 'risk') return 'Risk'
            if (type === 'idv_biometrics') return 'ID Verification / Biometrics'
            if (type === 'duplicate') return 'Duplicate Check'
            return titleCaseFromSnake(type)
          }

          const stepPrefix = `${indent}${si + 1}. `

          if (s.type === 'aml') {
            const details = formatAmlStepDetails()
            return `${stepPrefix}${stepLabel('aml')}\n${details
              .split('\n')
              .map((l) => `${indent}    ${l}`)
              .join('\n')}`
          }

          if (s.type === 'kyc') {
            const details = formatKycStepDetails(s)
            return `${stepPrefix}${stepLabel('kyc')}\n${details
              .split('\n')
              .map((l) => `${indent}    ${l}`)
              .join('\n')}`
          }

          if (s.type === 'idv_biometrics') {
            const details = formatIdvStepDetails(s)
            return `${stepPrefix}${stepLabel('idv_biometrics')}\n${details
              .split('\n')
              .map((l) => `${indent}    ${l}`)
              .join('\n')}`
          }

          if (s.type === 'duplicate') {
            const notesLine = s.notes ? `\n${indent}    Notes: ${s.notes}` : ''
            return `${stepPrefix}${stepLabel('duplicate')}${notesLine}`
          }

          if (s.type !== 'risk') return `${stepPrefix}${stepLabel(s.type)}`

          const decisionLine = `${stepPrefix}${stepLabel('risk')}`
          const branches = s.branches
            .map((b: RiskBranch, bi: number) => {
              const outcomeLabel = b.outcome === 'pass' ? ' >> Pass' : b.outcome === 'fail' ? ' >> Fail' : b.outcome === 'in_review' ? ' >> In Review' : ''
              const head = `${indent}    Branch ${bi + 1}${b.condition ? ` — ${b.condition}` : ''}${outcomeLabel}`
              const nested = formatWorkflowSteps(b.steps, `${indent}      `)
              return [head, nested].filter(Boolean).join('\n')
            })
            .join('\n')

          return [decisionLine, branches].filter(Boolean).join('\n')
        })
        .join('\n\n')
    }

    const workflowBaseLabel = pdfLabelFromSchema('recipes_workflow_builder_summary', 'Workflow')
    const workflowFieldLabel = `${workflowBaseLabel} ${idx + 1} (${w.name || 'Unnamed'})`

    if (w.kind === 'complex') {
      add({
        section: 'Recipes/Workflows',
        field: workflowFieldLabel,
        displayField: workflowFieldLabel,
        schemaFieldId: 'recipes_workflow_complex_summary',
        value: `Type=Complex\nWorkflow ID=${w.workflowId || '—'}\nURL=${w.url || '—'}\nNotes=${w.notes || '—'}`,
        pending: isEmpty(w.name) || isEmpty(w.workflowId) || isEmpty(w.url),
        guidance: undefined,
      })
      return
    }

    const stepsSummary = formatWorkflowSteps(w.steps, '') || '—'

    const v1Recipe = w.v1RecipeKey ? V1_RECIPES.find((r) => r.key === w.v1RecipeKey) : undefined
    const v1Block = v1Recipe
      ? `V1 Profile: ${v1Recipe.label} (${v1Recipe.key})\nAML Level: ${v1Recipe.amlLevel === 'enhanced' ? 'Enhanced (Media)' : 'Standard'}\nCheck Types: ${v1Recipe.checkTypes.join(', ')}\nCheck Order: ${v1Recipe.checkOrder.map((co, i) => `${i + 1}. ${co}`).join(' >> ')}\n\n`
      : ''

    const bw = w as BuilderWorkflow
    const v1CustomBlock = !bw.v1RecipeKey && Array.isArray(bw.v1CustomChecks) && bw.v1CustomChecks.length > 0
      ? `V1 Custom Profile\nCheck Types:\n${bw.v1CustomChecks.map((c, i) => {
          const label = V1_CHECK_TYPE_OPTIONS.find((o) => o.id === c.checkTypeId)?.label || c.checkTypeId
          const flags = [c.stopOnFail ? 'stop on fail' : '', c.optional ? 'optional' : ''].filter(Boolean).join(', ')
          return `${i + 1}. ${label}${flags ? ` (${flags})` : ''}`
        }).join('\n')}\n\n`
      : ''

    add({
      section: 'Recipes/Workflows',
      field: workflowFieldLabel,
      displayField: workflowFieldLabel,
      schemaFieldId: 'recipes_workflow_builder_summary',
      value: `${v1Block}${v1CustomBlock}Type: Builder${w.steps.length > 0 ? `\n\nSteps:\n${stepsSummary}` : ''}\n\nNotes: ${w.notes || '—'}`,
      pending: isEmpty(w.name) || (w.steps.length === 0 && !bw.v1RecipeKey && !(Array.isArray(bw.v1CustomChecks) && bw.v1CustomChecks.length > 0)),
      guidance: undefined,
    })
  })
  } // end upsell Workflows else

  // Rules
  const rules = scope.rulesConfig

  // Upsell: if Rules/Fraud config is untouched, collapse to a single summary row
  const rulesUntouched = (!rules.duplicateRules || rules.duplicateRules === 'unknown') &&
    (!rules.blocklistRules || rules.blocklistRules === 'unknown') &&
    (!rules.fraudEnabled || rules.fraudEnabled === 'unknown') &&
    (!rules.riskRatingSystem || rules.riskRatingSystem === 'unknown')
  if (isUpsell && rulesUntouched) {
    add({
      section: 'Rules',
      field: 'Rules & Fraud configuration',
      displayField: 'Rules & Fraud configuration',
      value: 'All existing Rules & Fraud settings apply — no changes',
      pending: false,
      skipPendingCheck: true,
    })
  } else {

  add({
    section: 'Advanced',
    field: 'Duplicate rules',
    displayField: pdfLabelFromSchema('rules_duplicate_rules_mode', 'Duplicate rules'),
    schemaFieldId: 'rules_duplicate_rules_mode',
    value: formatRuleChoiceLabel(rules.duplicateRules),
    pending: isEmpty(rules.duplicateRules) || rules.duplicateRules === 'unknown',
    guidance: undefined,
  })

  if (rules.duplicateRules === 'custom') {
    const ruleSet = rules.customDuplicateRuleSet || []
    if (ruleSet.length > 0) {
      const rulesSummary = ruleSet.map((r, i) => {
        const fields = r.matchFields.join(', ') || '(no fields)'
        return `${i + 1}. ${r.name || 'Unnamed'} — ${fields} [${r.method}, ${r.strength}]`
      }).join('\n')
      add({
        section: 'Advanced',
        field: 'Custom duplicate rules',
        displayField: 'Custom duplicate rules',
        schemaFieldId: 'rules_custom_duplicate_rules',
        value: rulesSummary,
        pending: false,
        guidance: undefined,
      })
    }
    if (rules.customDuplicateNotes) {
      add({
        section: 'Advanced',
        field: 'Custom duplicate notes',
        displayField: 'Custom duplicate notes',
        schemaFieldId: 'rules_custom_duplicate_rules',
        value: rules.customDuplicateNotes,
        pending: false,
        guidance: undefined,
      })
    }
    if (ruleSet.length === 0 && !rules.customDuplicateNotes) {
      add({
        section: 'Advanced',
        field: 'Custom duplicate rules',
        displayField: 'Custom duplicate rules',
        schemaFieldId: 'rules_custom_duplicate_rules',
        value: rules.customDuplicateRules || '',
        pending: true,
        guidance: undefined,
      })
    }
  }

  add({
    section: 'Advanced',
    field: 'Blocklist rules',
    displayField: pdfLabelFromSchema('rules_blocklist_rules_mode', 'Blocklist rules'),
    schemaFieldId: 'rules_blocklist_rules_mode',
    value: formatRuleChoiceLabel(rules.blocklistRules),
    pending: isEmpty(rules.blocklistRules) || rules.blocklistRules === 'unknown',
    guidance: undefined,
  })

  if (rules.blocklistRules === 'custom') {
    const entries = rules.customBlocklistEntries || []
    if (entries.length > 0) {
      const entriesSummary = entries.map((e, i) => {
        const score = e.riskScore != null ? `, Risk: ${e.riskScore > 0 ? '+' : ''}${e.riskScore}` : ''
        return `${i + 1}. ${e.name || 'Unnamed'} — ${e.dataType} >> ${e.action}${score}${e.description ? ` (${e.description})` : ''}`
      }).join('\n')
      add({
        section: 'Advanced',
        field: 'Custom match lists',
        displayField: 'Custom match lists',
        schemaFieldId: 'rules_custom_blocklist_rules',
        value: entriesSummary,
        pending: false,
        guidance: undefined,
      })
    }
    if (rules.customBlocklistNotes) {
      add({
        section: 'Advanced',
        field: 'Custom blocklist notes',
        displayField: 'Custom blocklist notes',
        schemaFieldId: 'rules_custom_blocklist_rules',
        value: rules.customBlocklistNotes,
        pending: false,
        guidance: undefined,
      })
    }
    if (entries.length === 0 && !rules.customBlocklistNotes) {
      add({
        section: 'Advanced',
        field: 'Custom blocklist rules',
        displayField: 'Custom blocklist rules',
        schemaFieldId: 'rules_custom_blocklist_rules',
        value: rules.customBlocklistRules || '',
        pending: true,
        guidance: undefined,
      })
    }
  }

  // Fraud & Transaction Monitoring
  {
    const fraudLabels: Record<string, string> = { yes: 'Yes', no: 'No', unknown: formatDontKnow() }
    add({
      section: 'Rules',
      field: 'Fraud enabled',
      displayField: pdfLabelFromSchema('rules_fraud_enabled', 'Fraud enabled'),
      schemaFieldId: 'rules_fraud_enabled',
      value: fraudLabels[rules.fraudEnabled as string] || formatTriStateLabel(rules.fraudEnabled),
      pending: !rules.fraudEnabled || rules.fraudEnabled === 'unknown',
      guidance: undefined,
    })

    if (rules.fraudEnabled === 'yes' && rules.fraudTypes && rules.fraudTypes.length > 0) {
      const fraudTypeLabels: Record<string, string> = {
        device_intelligence: 'Device Intelligence',
        behavioural_biometrics: 'Behavioural Biometrics',
        phone_risk: 'Phone Risk Assessment',
        email_risk: 'Email Risk Assessment',
        transaction_monitoring: 'Transaction & Activity Monitoring',
      }
      add({
        section: 'Rules',
        field: 'Fraud types',
        displayField: pdfLabelFromSchema('rules_fraud_types', 'Fraud types'),
        schemaFieldId: 'rules_fraud_types',
        value: rules.fraudTypes.map((t) => fraudTypeLabels[t] || t).join(', '),
        pending: false,
        guidance: undefined,
      })
    }

    if (rules.fraudRequirements?.trim()) {
      add({
        section: 'Rules',
        field: 'Fraud requirements',
        displayField: pdfLabelFromSchema('rules_fraud_requirements', 'Fraud requirements'),
        schemaFieldId: 'rules_fraud_requirements',
        value: rules.fraudRequirements,
        pending: false,
        guidance: undefined,
      })
    }
  }

  // Risk Rating System — only include if explicitly set (Advanced tab fields)
  {
    const riskSystemLabels: Record<string, string> = { unknown: formatDontKnow(), frankieone: 'FrankieOne', theirs: "The Client's", both: 'Both' }
    if (rules.riskRatingSystem && rules.riskRatingSystem !== 'unknown') {
      add({
        section: 'Risk',
        field: 'Risk rating system',
        displayField: pdfLabelFromSchema('rules_risk_rating_system', 'Risk rating system'),
        schemaFieldId: 'rules_risk_rating_system',
        value: riskSystemLabels[rules.riskRatingSystem] || rules.riskRatingSystem,
        pending: false,
        guidance: undefined,
      })
    }

    const riskChangeLabels: Record<string, string> = { unknown: formatDontKnow(), changing: 'Changing', not_changing: 'Not changing' }
    if (rules.riskSystemChange && rules.riskSystemChange !== 'unknown') {
      add({
        section: 'Risk',
        field: 'Changing risk system',
        displayField: pdfLabelFromSchema('rules_risk_system_change', 'Changing risk system'),
        schemaFieldId: 'rules_risk_system_change',
        value: riskChangeLabels[rules.riskSystemChange] || rules.riskSystemChange,
        pending: false,
        guidance: undefined,
      })
    }
  }

  } // end upsell Rules else

  // Risk Tables removed from PDF — risk configuration is now just a notes field

  // SOW Configuration, Feature Requests, Scope Hours, Notes — JSON export only
  if (!includeNonConfig) {
    _isUpsellExport = false
    if (isUpsell) rows.forEach((r) => { r.pending = false })
    return rows
  }

  const sow = scope.sowConfig
  if (sow) {
    if (sow.deliveryNotes?.trim()) {
      add({
        section: 'SOW',
        field: 'Delivery notes',
        displayField: pdfLabelFromSchema('sow_delivery_notes_content', 'Delivery notes'),
        schemaFieldId: 'sow_delivery_notes_content',
        value: sow.deliveryNotes,
        pending: false,
        guidance: undefined,
      })
    }

    if (sow.milestones && sow.milestones.length > 0) {
      add({
        section: 'SOW',
        field: 'Milestones',
        displayField: pdfLabelFromSchema('sow_milestones', 'Milestones'),
        schemaFieldId: 'sow_milestones',
        value: sow.milestones
          .map((m) => `${m.name}${m.date ? ` (${m.date})` : ''}${m.description ? `: ${m.description}` : ''}`)
          .join('\n'),
        pending: false,
        guidance: undefined,
      })
    }

    if (sow.phases && sow.phases.length > 0) {
      add({
        section: 'SOW',
        field: 'Phases',
        displayField: pdfLabelFromSchema('sow_phases', 'Phases'),
        schemaFieldId: 'sow_phases',
        value: sow.phases
          .map((p) => {
            const deliverables = p.deliverables?.length ? `\n  Deliverables: ${p.deliverables.join(', ')}` : ''
            return `${p.name}${p.duration ? ` (${p.duration})` : ''}${p.description ? `: ${p.description}` : ''}${deliverables}`
          })
          .join('\n'),
        pending: false,
        guidance: undefined,
      })
    }

    {
      const V2_CUTOFF = '2026-08-31'
      const v2Feats: string[] = []
      if (scope.kybConfig?.internationalEnabled === 'yes') v2Feats.push('International KYB')
      if (scope.kybConfig?.trustAnalyserEnabled === 'yes') v2Feats.push('Trust Analyser')
      if (scope.kybConfig?.onboardingFormEnabled === 'yes') v2Feats.push('Business Onboarding Form')
      if (scope.kybConfig?.amlEntityScreening === 'yes') v2Feats.push('Business AML')
      const goLiveBefore = scope.generalInfo?.goLiveDate && scope.generalInfo.goLiveDate < V2_CUTOFF
      const v2Note = v2Feats.length > 0 && goLiveBefore
        ? `The following features are currently in development and will not be available for implementation as part of the initial go-live: ${v2Feats.join(', ')}. These features will be delivered in a subsequent phase once generally available.`
        : null
      const combined = [sow.keyAssumptions?.trim(), v2Note].filter(Boolean).join('\n\n')
      if (combined) {
        add({
          section: 'SOW',
          field: 'Key assumptions',
          displayField: pdfLabelFromSchema('sow_key_assumptions_content', 'Key assumptions'),
          schemaFieldId: 'sow_key_assumptions_content',
          value: combined,
          pending: false,
          guidance: undefined,
        })
      }
    }

    if (sow.notes?.trim()) {
      add({
        section: 'SOW',
        field: 'SOW notes',
        displayField: pdfLabelFromSchema('sow_notes', 'SOW notes'),
        schemaFieldId: 'sow_notes',
        value: sow.notes,
        pending: false,
        guidance: undefined,
      })
    }
  }

  // Feature Requests
  if (scope.featureRequests && scope.featureRequests.length > 0) {
    add({
      section: 'Feature Requests',
      field: 'Feature requests',
      displayField: pdfLabelFromSchema('feature_requests_summary', 'Feature requests'),
      schemaFieldId: 'feature_requests_summary',
      value: scope.featureRequests
        .map((f, i) => `${i + 1}. ${f.name}${f.hours ? ` (${f.hours}h)` : ''}${f.description ? `\n   ${f.description}` : ''}`)
        .join('\n'),
      pending: false,
      guidance: undefined,
    })
  }

  // Scope Hours
  if (scope.scopeHours && scope.scopeHours.length > 0) {
    add({
      section: 'Scope Hours',
      field: 'Scope hours',
      displayField: pdfLabelFromSchema('scope_hours_summary', 'Scope hours'),
      schemaFieldId: 'scope_hours_summary',
      value: scope.scopeHours
        .map((h) => `${h.section}: ${h.description} — ${h.estimatedHours}h (${titleCaseFromSnake(h.complexity)})`)
        .join('\n'),
      pending: false,
      guidance: undefined,
    })
  }

  // Notes
  if (scope.notes?.trim()) {
    add({
      section: 'Notes',
      field: 'Notes',
      displayField: pdfLabelFromSchema('scope_notes', 'Notes'),
      schemaFieldId: 'scope_notes',
      value: scope.notes,
      pending: false,
      guidance: undefined,
    })
  }

  _isUpsellExport = false
  if (isUpsell) rows.forEach((r) => { r.pending = false })
  return rows
}

/**
 * Render the grouped-section scope summary tables into an existing jsPDF doc,
 * starting at `startY`. Returns the Y position after the last table.
 *
 * Shared by `exportScopeToPdf` (scope-only export) and `exportSowToPdf`
 * (SOW header followed by the full scope summary).
 */
const renderScopeSummaryTables = (
  doc: jsPDF,
  scope: ScopeData,
  drawLogo: () => void,
  startY: number,
  pageWidth: number,
  pageHeight: number
): number => {
  const brandPrimaryRgb = hexToRgb(BRAND.primary)
  const brandLightRgb = hexToRgb(BRAND.light)

  const rows = buildExportRows(scope).map((r) => {
    const pending = r.skipPendingCheck ? Boolean(r.pending) : (Boolean(r.pending) || isDecisionRequiredValue(r.value))
    return { ...r, pending }
  })

  const groupedUnordered = rows.reduce<Record<string, ExportRow[]>>((acc, r) => {
    acc[r.section] ||= []
    acc[r.section].push(r)
    return acc
  }, {})

  const sectionOrder = [
    'General',
    'KYC',
    'AML',
    'KYB',
    'ID Verification',
    'Biometrics',
    'Recipes/Workflows',
    'Custom Work',
    'Advanced',
    'Rules',
    'Risk',
    'SOW',
  ]
  const grouped: Record<string, ExportRow[]> = {}
  for (const s of sectionOrder) {
    if (groupedUnordered[s]) grouped[s] = groupedUnordered[s]
  }
  for (const s of Object.keys(groupedUnordered)) {
    if (!grouped[s]) grouped[s] = groupedUnordered[s]
  }

  let cursorY = startY

  const ensureSpace = (requiredHeight: number) => {
    const bottomMargin = 60
    if (cursorY + requiredHeight <= pageHeight - bottomMargin) return
    doc.addPage()
    drawLogo()
    cursorY = 90
  }

  Object.entries(grouped).forEach(([section, rawSectionRows], idx) => {
    let sectionRows = rawSectionRows
    if (section === 'Advanced' || section === 'Custom Work') {
      const trivialValues = new Set(['', '—', "Don't Know", 'Existing Settings', 'unknown', 'Yes', 'No', 'Parent Only', 'Parent + Child'])
      const isRowTrivial = (r: ExportRow) => {
        const v = (r.value || '').trim()
        if (trivialValues.has(v)) return true
        if (r.pending) return true
        if (r.field.toLowerCase().includes('aging') && (v === '12 months (1 year)' || v === 'No')) return true
        return false
      }
      sectionRows = rawSectionRows.filter((r) => !isRowTrivial(r))
      if (sectionRows.length === 0) return
    }

    if (idx > 0) cursorY += 10
    ensureSpace(40)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.text(sectionRows[0]?.displaySection || section, 40, cursorY)

    cursorY += 10

    const noInfoColumn = section === 'Recipes/Workflows'
    const contentWidth = pageWidth - 40 - 40

    autoTable(doc, {
      startY: cursorY,
      margin: { top: 80 },
      head: [noInfoColumn ? ['Field', 'Selected'] : ['Field', 'Selected', 'Information']],
      body: sectionRows.map((r) => {
        if (noInfoColumn) return [r.displayField || r.field, r.value || '—']
        const decisionSummary = r.decisionSummary ?? ''
        const guidance = r.guidance ?? getGuidance(r.section, r.field)
        const info = r.pending
          ? (guidance || 'Decision required.')
          : (decisionSummary || '')
        return [r.displayField || r.field, r.value || '—', info]
      }),
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, valign: 'top', overflow: 'linebreak', halign: 'left' },
      headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: brandLightRgb },
      columnStyles: noInfoColumn
        ? {
            0: { cellWidth: 150, halign: 'left' },
            1: { cellWidth: contentWidth - 150, halign: 'left' },
          }
        : {
            0: { cellWidth: 150, halign: 'left' },
            1: { cellWidth: 120, halign: 'left' },
            2: { cellWidth: contentWidth - 150 - 120, halign: 'left' },
          },
      didDrawPage: () => {
        drawLogo()
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const row = sectionRows[data.row.index]
          if (row?.pending) {
            data.cell.styles.textColor = BRAND.warning
            data.cell.styles.fillColor = [255, 247, 237]
            if (data.column.index === 0) data.cell.styles.fontStyle = 'bold'
          }
        }
      },
    })

    // @ts-expect-error - autotable plugin
    cursorY = doc.lastAutoTable.finalY + 16
  })

  return cursorY
}

export const exportScopeToPdf = async (scope: ScopeData) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })

  const brandPrimaryRgb = hexToRgb(BRAND.primary)
  const logo = await getImageDataUrlFromPublicWebp('/FrankieOne-MainLogo-2.webp')

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const drawLogo = () => {
    if (!logo) return

    const margin = 40
    const maxW = 95
    const maxH = 22

    const scale = Math.min(maxW / logo.width, maxH / logo.height)
    const w = Math.max(1, logo.width * scale)
    const h = Math.max(1, logo.height * scale)

    doc.addImage(logo.dataUrl, 'PNG', pageWidth - margin - w, 30, w, h)
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text(pdfLabelFromSchema('general_pdf_document_title', 'Scope Configuration Summary'), 40, 50)
  doc.setTextColor(0, 0, 0)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(
    pdfGuidanceFromSchemaWithOptions('general_pdf_document_intro') ||
      'This document summarises the choices made so far, and highlights decisions that still need to be made.',
    40,
    70
  )

  drawLogo()

  // Legend / key
  doc.setFontSize(9)
  const legendY = 110
  doc.setFillColor(...BRAND.warning)
  doc.rect(40, legendY - 8, 10, 10, 'F')
  doc.setTextColor(0, 0, 0)
  doc.text(
    pdfGuidanceFromSchemaWithOptions('general_pdf_pending_legend') ||
      'Highlighted items indicate a decision is still required (empty / unknown / tbd).',
    56,
    legendY
  )

  const topBlockY = legendY + 20

  const pendingCount = buildExportRows(scope).filter(isVisiblyPending).length

  doc.setFontSize(11)
  doc.text(`Customer: ${scope.customerName || '—'}`, 40, topBlockY)
  doc.text(`Status: ${titleCaseFromSnake(scope.generalInfo.scopeStatus || 'draft')}${scope.finalized ? ' (Finalised)' : ''}`, 40, topBlockY + 16)
  doc.text(`Pending decisions: ${pendingCount}`, 40, topBlockY + 32)

  renderScopeSummaryTables(doc, scope, drawLogo, topBlockY + 60, pageWidth, pageHeight)

  const safeName = (scope.customerName || 'scope').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 50)
  const filename = `${safeName}_scope_summary.pdf`
  doc.save(filename)
}

// =====================================================
// STATEMENT OF WORK PDF
// =====================================================

/**
 * Export a Statement of Work PDF. The SOW details (customer, legal preamble,
 * delivery notes, phases, milestones, RACI, key assumptions) are rendered
 * first, followed by the full scope configuration summary on subsequent pages.
 */

// ── Pure-SVG sequence diagram renderer ────────────────────────────────────
// Generates an SVG string using mathematically-positioned elements so text
// never drifts (unlike html2canvas which approximates CSS layout).
// Layout constants MUST match SequenceDiagram.tsx.

function escSvg(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function truncSvg(text: string, maxPx: number, charPx = 6.5): string {
  const max = Math.floor(maxPx / charPx)
  return text.length <= max ? text : text.slice(0, max - 1) + '…'
}

function methodFill(m: string): string {
  if (m === 'GET') return '#3b82f6'
  if (m === 'POST') return '#22c55e'
  if (m === 'PUT') return '#f59e0b'
  if (m === 'DELETE') return '#ef4444'
  return '#9ca3af'
}

export function flowToSvg(flow: FlowDefinition): { svg: string; width: number; height: number } {
  const COL = 230, MSG_H = 38, MSG_H_CARD = 64, MSG_H_RETURN = 50
  const SELF_H = 28, SPACER_H = 6, HEADER_H = 44, ARR = 7
  const LINE_H = 15  // 11.5px font × 1.3 line-height ≈ 15px

  const pts = flow.participants
  const n = pts.length
  const baseW = n * COL
  const cx = (i: number) => i * COL + COL / 2

  // Compute minimum width so labels and URLs are not cropped
  function computeMinW(steps: DiagramStep[]): number {
    let minW = baseW
    for (const step of steps) {
      if (step.type === 'message') {
        const m = step as MessageStep
        const fi = pts.findIndex(p => p.id === m.from)
        const ti = pts.findIndex(p => p.id === m.to)
        if (fi >= 0 && ti >= 0) {
          const fx = cx(fi), tx = cx(ti)
          const midX = (fx + tx) / 2
          const lineW = Math.abs(tx - fx)
          let cardW = m.label.length * 6.5 + 24
          if (m.sublabel) cardW = Math.max(cardW, m.sublabel.length * 6 + 20)
          if (m.endpoint && !m.isReturn) {
            const endpointW = 8 + m.endpoint.method.length * 6 + 16 + 6 + m.endpoint.url.length * 6 + 12
            cardW = Math.max(cardW, endpointW)
          }
          const lw = Math.max(lineW * 1.5, 180, cardW)
          const lx = Math.max(8, midX - lw / 2)
          minW = Math.max(minW, lx + lw + 16)
        }
      }
      if (step.type === 'self') {
        const s = step as SelfStep
        const idx = pts.findIndex(p => p.id === s.participant)
        if (idx >= 0) {
          const x = cx(idx)
          const textW = s.label.length * 6 + 20
          minW = Math.max(minW, x + 26 + 10 + textW + 16)
        }
      }
      if (step.type === 'group') {
        minW = Math.max(minW, computeMinW((step as GroupStep).steps as DiagramStep[]))
      }
    }
    return minW
  }

  const W = computeMinW(flow.steps)

  // Word-wrap text to fit within maxPx, returning an array of lines.
  // charPx is the average character width for the given font/size.
  function wrapText(text: string, maxPx: number, charPx = 6.5): string[] {
    const maxChars = Math.max(1, Math.floor(maxPx / charPx))
    const words = text.split(' ')
    const lines: string[] = []
    let cur = ''
    for (const word of words) {
      const test = cur ? `${cur} ${word}` : word
      if (test.length <= maxChars) {
        cur = test
      } else {
        if (cur) lines.push(cur)
        // Word longer than line? hard-break it
        cur = word.length > maxChars ? `${word.slice(0, maxChars - 1)}…` : word
      }
    }
    if (cur) lines.push(cur)
    return lines.length > 0 ? lines : ['']
  }

  // Compute the label column width for a message step
  function labelW(s: MessageStep): number {
    const fi = pts.findIndex(p => p.id === s.from)
    const ti = pts.findIndex(p => p.id === s.to)
    if (fi < 0 || ti < 0) return 180
    const fx = cx(fi), tx = cx(ti)
    return Math.max(Math.abs(tx - fx) * 1.5, 180)
  }

  function stepH(s: DiagramStep): number {
    if (s.type === 'spacer') return SPACER_H
    if (s.type === 'self') return SELF_H
    if (s.type === 'note') return 16 + (s as NoteStep).lines.length * 17 + 10 + 12
    if (s.type === 'message') {
      const m = s as MessageStep
      const isReturn = m.isReturn ?? false
      const isApi = !!m.endpoint && !isReturn
      const lw = labelW(m)
      const textMaxPx = lw - (isApi ? 20 : 10)
      const extraLines = Math.max(0, wrapText(m.label, textMaxPx).length - 1)
      if (isApi) return MSG_H_CARD + extraLines * LINE_H
      if (isReturn) return MSG_H_RETURN + extraLines * LINE_H
      return MSG_H + extraLines * LINE_H
    }
    if (s.type === 'group') {
      const g = s as GroupStep
      return 4 + 10 + g.steps.reduce((a, x) => a + stepH(x as DiagramStep), 0) + 4 + 4
    }
    return 0
  }

  const totalBodyH = flow.steps.reduce((a, s) => a + stepH(s), 0) + 10
  const H = HEADER_H + totalBodyH

  const o: string[] = []
  const $ = (s: string) => o.push(s)

  $(`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" style="background:white;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">`)

  // ── Header band ──
  $(`<rect x="0" y="0" width="${W}" height="${HEADER_H}" fill="#f8fafc"/>`)
  $(`<line x1="0" y1="${HEADER_H}" x2="${W}" y2="${HEADER_H}" stroke="#e5e7eb" stroke-width="1"/>`)
  for (let i = 1; i < n; i++) {
    $(`<line x1="${i * COL}" y1="0" x2="${i * COL}" y2="${HEADER_H}" stroke="#f1f5f9" stroke-width="1"/>`)
  }
  pts.forEach((p, i) => {
    const mx = cx(i)
    const chipW = Math.min(Math.max(p.label.length * 7 + 24, 80), COL - 20)
    const chipH = 22, chipX = mx - chipW / 2, chipY = (HEADER_H - chipH) / 2
    $(`<rect x="${chipX.toFixed(1)}" y="${chipY.toFixed(1)}" width="${chipW.toFixed(1)}" height="${chipH}" rx="11" fill="${p.headerBg}" stroke="${p.headerBorder}" stroke-width="1"/>`)
    $(`<text x="${mx}" y="${(chipY + chipH / 2).toFixed(1)}" text-anchor="middle" dominant-baseline="central" font-size="11" font-weight="600" fill="${p.headerText}">${escSvg(p.label)}</text>`)
    if (p.sublabel) {
      $(`<text x="${mx}" y="${HEADER_H - 5}" text-anchor="middle" dominant-baseline="auto" font-size="9" fill="#9ca3af">${escSvg(p.sublabel)}</text>`)
    }
  })

  // ── Lifelines ──
  for (let i = 0; i < n; i++) {
    $(`<line x1="${cx(i)}" y1="${HEADER_H}" x2="${cx(i)}" y2="${H - 10}" stroke="#e5e7eb" stroke-width="1.5"/>`)
  }

  // ── Bottom dots ──
  for (let i = 0; i < n; i++) {
    $(`<circle cx="${cx(i)}" cy="${H - 5}" r="3.5" fill="#d1d5db"/>`)
  }

  // ── Arrow helper ──
  function arrow(fx: number, tx: number, y: number, stroke: string, sw: number, dash?: string) {
    const da = dash ? ` stroke-dasharray="${dash}"` : ''
    $(`<line x1="${fx}" y1="${y}" x2="${tx}" y2="${y}" stroke="${stroke}" stroke-width="${sw}"${da}/>`)
    const half = ARR * 0.65
    if (tx > fx) {
      $(`<polygon points="${tx},${y} ${tx - ARR - 1},${y - half} ${tx - ARR - 1},${y + half}" fill="${stroke}"/>`)
    } else {
      $(`<polygon points="${tx},${y} ${tx + ARR + 1},${y - half} ${tx + ARR + 1},${y + half}" fill="${stroke}"/>`)
    }
  }

  // ── Step renderer ──
  function drawStep(step: DiagramStep, y: number) {
    if (step.type === 'spacer') return

    if (step.type === 'message') {
      const s = step as MessageStep
      const fi = pts.findIndex(p => p.id === s.from)
      const ti = pts.findIndex(p => p.id === s.to)
      if (fi < 0 || ti < 0) return

      const fx = cx(fi), tx = cx(ti)
      const isReturn = s.isReturn ?? false
      const isAsync = s.isAsync ?? false
      const isApi = !!s.endpoint && !isReturn

      const mid = (fx + tx) / 2
      const lw = Math.min(Math.max(Math.abs(tx - fx) * 1.5, 180), W - 24)
      const lx = Math.max(8, mid - lw / 2)
      const lcx = lx + lw / 2

      // Compute rowH using the same wrapping as stepH so arrow lands at the right Y
      const labelMargin = isApi ? 20 : 10
      const labelLineCount = wrapText(s.label, lw - labelMargin, 6.5).length
      const extraLines = Math.max(0, labelLineCount - 1)
      const baseH = isApi ? MSG_H_CARD : isReturn ? MSG_H_RETURN : MSG_H
      const rowH = baseH + extraLines * LINE_H

      const stroke = isReturn ? '#9ca3af' : '#4338ca'
      const dash = (isReturn || isAsync) ? '5,4' : undefined
      arrow(fx, tx, y + rowH - 10, stroke, isApi ? 1.75 : 1.5, dash)

      if (isApi) {
        const mColor = methodFill(s.endpoint!.method)
        const tints: Record<string, string> = {
          GET: 'rgba(59,130,246,0.12)', POST: 'rgba(34,197,94,0.13)',
          PUT: 'rgba(245,158,11,0.13)', DELETE: 'rgba(239,68,68,0.13)',
        }
        const cardY = y + 4, cardH = rowH - 20
        // Compute card width — expand to fit the full URL (capped at diagram edge)
        const method = s.endpoint!.method
        const bw = method.length * 6 + 12
        const bx = lx + 12
        const urlX = bx + bw + 6
        const urlNaturalW = s.endpoint!.url.length * 5.5 + 8
        const cardW = Math.min(Math.max(lw, urlX - lx + urlNaturalW), W - lx - 4)
        const cardCX = lx + cardW / 2
        $(`<rect x="${lx.toFixed(1)}" y="${cardY}" width="${cardW.toFixed(1)}" height="${cardH}" rx="6" fill="${tints[s.endpoint!.method] ?? 'rgba(99,102,241,0.12)'}" stroke="${mColor}66" stroke-width="1.5"/>`)
        $(`<rect x="${lx.toFixed(1)}" y="${cardY}" width="4" height="${cardH}" fill="${mColor}"/>`)
        // Wrapped label lines — first line always at cardY+16, additional lines below
        const labelLines = wrapText(s.label, cardW - 20, 6.5)
        labelLines.forEach((line, li) => {
          $(`<text x="${cardCX}" y="${cardY + 16 + li * LINE_H}" text-anchor="middle" dominant-baseline="central" font-size="11.5" font-weight="600" fill="#1e1b4b">${escSvg(line)}</text>`)
        })
        // Method badge + URL — full URL, no truncation
        const badgeY = cardY + 28 + (labelLines.length - 1) * LINE_H
        $(`<rect x="${bx.toFixed(1)}" y="${badgeY}" width="${bw}" height="14" rx="2" fill="${mColor}"/>`)
        $(`<text x="${(bx + bw / 2).toFixed(1)}" y="${badgeY + 7}" text-anchor="middle" dominant-baseline="central" font-size="9" font-weight="700" fill="white">${method}</text>`)
        $(`<text x="${urlX.toFixed(1)}" y="${badgeY + 7}" dominant-baseline="central" font-size="10" font-family="monospace" fill="#52525b">${escSvg(s.endpoint!.url)}</text>`)
      } else if (isReturn) {
        const cardY = y + 4, cardH = rowH - 20
        $(`<rect x="${lx.toFixed(1)}" y="${cardY}" width="${lw.toFixed(1)}" height="${cardH}" rx="6" fill="#e4e4e7" stroke="#d4d4d8" stroke-width="1.5"/>`)
        // For return cards, first line center is always at row_y + 19
        const lines = wrapText(s.label, lw - 20, 6.5)
        lines.forEach((line, li) => {
          $(`<text x="${lcx}" y="${y + 19 + li * LINE_H}" text-anchor="middle" dominant-baseline="central" font-size="11.5" font-weight="400" fill="#52525b">${escSvg(line)}</text>`)
        })
      } else {
        // Regular: first line center always at row_y + 13 regardless of line count
        const lines = wrapText(s.label, lw - 10, 6.5)
        lines.forEach((line, li) => {
          $(`<text x="${lcx}" y="${y + 13 + li * LINE_H}" text-anchor="middle" dominant-baseline="central" font-size="11.5" font-weight="600" fill="#1e1b4b">${escSvg(line)}</text>`)
        })
        if (s.sublabel) {
          const sub = truncSvg(s.sublabel, lw - 10, 5.5)
          $(`<text x="${lcx}" y="${y + 13 + lines.length * LINE_H}" text-anchor="middle" dominant-baseline="central" font-size="10" font-family="monospace" fill="#6366f1">${escSvg(sub)}</text>`)
        }
      }
      return
    }

    if (step.type === 'self') {
      const s = step as SelfStep
      const idx = pts.findIndex(p => p.id === s.participant)
      if (idx < 0) return
      const x = cx(idx), lw = 26, lh = 14, my = y + SELF_H / 2
      $(`<rect x="${x}" y="${my - lh / 2}" width="${lw}" height="${lh}" rx="3" fill="none" stroke="#d1d5db" stroke-width="1.5"/>`)
      $(`<polygon points="${x},${my + lh / 2} ${x + 8},${my + lh / 2 - 4} ${x + 8},${my + lh / 2 + 4}" fill="#d1d5db"/>`)
      const txt = truncSvg(s.label, W - (x + lw + 16), 6)
      $(`<text x="${x + lw + 10}" y="${my}" dominant-baseline="central" font-size="11" font-style="italic" fill="#6b7280">${escSvg(txt)}</text>`)
      return
    }

    if (step.type === 'note') {
      const s = step as NoteStep
      const idx = pts.findIndex(p => p.id === s.participant)
      if (idx < 0) return
      const x = cx(idx)
      const nw = Math.min(COL * 1.9, W - x - 12)
      const nh = 16 + s.lines.length * 17 + 10
      const nx = x + 12, ny = y + 6
      $(`<rect x="${nx}" y="${ny}" width="${nw}" height="${nh}" rx="6" fill="#fefce8" stroke="#fde68a" stroke-width="1"/>`)
      s.lines.forEach((line, li) => {
        $(`<text x="${nx + 10}" y="${ny + 14 + li * 17}" dominant-baseline="central" font-size="11" fill="#713f12">${escSvg(line)}</text>`)
      })
      return
    }

    if (step.type === 'group') {
      const s = step as GroupStep
      const innerH = s.steps.reduce((a, x) => a + stepH(x as DiagramStep), 0)
      const gy = y + 4, gh = 10 + innerH + 4
      $(`<rect x="4" y="${gy}" width="${W - 8}" height="${gh}" rx="8" fill="none" stroke="#d1d5db" stroke-width="2" stroke-dasharray="6,4"/>`)
      const bw = s.condition.length * 7 + 20
      $(`<rect x="14" y="${gy - 10}" width="${bw}" height="20" rx="4" fill="white" stroke="#d1d5db" stroke-width="1"/>`)
      $(`<text x="24" y="${gy}" dominant-baseline="central" font-size="11" font-weight="700" fill="#374151">${escSvg(s.condition)}</text>`)
      let iy = gy + 10
      for (const inner of s.steps) {
        drawStep(inner as DiagramStep, iy)
        iy += stepH(inner as DiagramStep)
      }
      return
    }
  }

  let y = HEADER_H
  for (const step of flow.steps) {
    drawStep(step, y)
    y += stepH(step)
  }

  $('</svg>')
  return { svg: o.join('\n'), width: W, height: H }
}

export async function svgToPng(svg: string, natW: number, natH: number, scale = 2, format: 'png' | 'jpeg' = 'png', quality = 0.85): Promise<string> {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = reject
      img.src = url
    })
    const canvas = document.createElement('canvas')
    canvas.width = natW * scale
    canvas.height = natH * scale
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL(`image/${format}`, format === 'jpeg' ? quality : undefined)
  } finally {
    URL.revokeObjectURL(url)
  }
}

async function sequenceDiagramToPng(
  flow: FlowDefinition,
  targetWidthPx: number,
): Promise<{ dataUrl: string; width: number; height: number } | null> {
  try {
    const { svg, width: natW, height: natH } = flowToSvg(flow)
    // Scale 1 + JPEG at 80% quality for SOW PDF — keeps file size manageable
    const dataUrl = await svgToPng(svg, natW, natH, 1, 'jpeg', 0.8)
    const ratio = Math.min(1, targetWidthPx / natW)
    return {
      dataUrl,
      width: Math.round(natW * ratio),
      height: Math.round(natH * ratio),
    }
  } catch {
    return null
  }
}

export const exportSowToPdf = async (scope: ScopeData) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })

  const brandPrimaryRgb = hexToRgb(BRAND.primary)
  const brandLightRgb = hexToRgb(BRAND.light)
  const logo = await getImageDataUrlFromPublicWebp('/FrankieOne-MainLogo-2.webp')

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const leftMargin = 40
  const rightMargin = 40
  const contentWidth = pageWidth - leftMargin - rightMargin

  const drawLogo = () => {
    if (!logo) return
    const maxW = 95
    const maxH = 22
    const scale = Math.min(maxW / logo.width, maxH / logo.height)
    const w = Math.max(1, logo.width * scale)
    const h = Math.max(1, logo.height * scale)
    doc.addImage(logo.dataUrl, 'PNG', pageWidth - rightMargin - w, 30, w, h)
  }

  let cursorY = 50

  const ensureSpace = (requiredHeight: number) => {
    const bottomMargin = 60
    if (cursorY + requiredHeight <= pageHeight - bottomMargin) return
    doc.addPage()
    drawLogo()
    cursorY = 90
  }

  const writeParagraph = (text: string, fontSize = 10, bold = false) => {
    if (!text) return
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(fontSize)
    doc.setTextColor(0, 0, 0)
    const lines = doc.splitTextToSize(text, contentWidth)
    const lineHeight = fontSize * 1.35
    // Render line-by-line so paragraphs can flow across page boundaries
    // instead of leaving a giant gap when the whole block won't fit.
    for (const line of lines) {
      ensureSpace(lineHeight)
      doc.text(line, leftMargin, cursorY)
      cursorY += lineHeight
    }
    cursorY += 4
  }

  const writeSectionHeading = (text: string) => {
    // Reserve space for heading + a couple of body lines so the heading
    // never gets stranded at the bottom of a page.
    ensureSpace(60)
    cursorY += 8
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(13)
    doc.setTextColor(...brandPrimaryRgb)
    doc.text(text, leftMargin, cursorY)
    doc.setTextColor(0, 0, 0)
    cursorY += 6
    doc.setDrawColor(...brandPrimaryRgb)
    doc.setLineWidth(1)
    doc.line(leftMargin, cursorY, leftMargin + contentWidth, cursorY)
    cursorY += 14
  }

  // ── Title ──
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text('Statement of Work', leftMargin, cursorY)
  doc.setTextColor(0, 0, 0)
  drawLogo()
  cursorY += 26

  // Customer subtitle
  const abn = (scope.customerAbn || '').trim()
  const customerLine = `${scope.customerName || '—'}${abn ? `  (ABN: ${abn})` : ''}`
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.text(customerLine, leftMargin, cursorY)
  cursorY += 16

  doc.setFontSize(9)
  doc.setTextColor(100, 100, 100)
  doc.text(`Generated ${new Date().toLocaleDateString()}`, leftMargin, cursorY)
  doc.setTextColor(0, 0, 0)
  cursorY += 18

  const sow = scope.sowConfig
  const gi = scope.generalInfo

  // ── Legal Text (with substituted tokens) ──
  if ((sow.legalText || '').trim()) {
    writeSectionHeading('Legal Text')
    const substituted = substituteLegalTextPlaceholders(sow.legalText, {
      customerName: scope.customerName,
      customerAbn: scope.customerAbn,
      issueDate: new Date(),
    })
    writeParagraph(substituted, 10)
  }

  // ── Delivery Notes + Key Dates ──
  const hasDeliveryNotes = (sow.deliveryNotes || '').trim().length > 0
  const hasDates = Boolean((gi.productionAccessDate || '').trim() || (gi.goLiveDate || '').trim())
  if (hasDeliveryNotes || hasDates) {
    writeSectionHeading('Delivery Notes')
    if (hasDeliveryNotes) writeParagraph(sow.deliveryNotes, 10)
    if (hasDates) {
      const rows: string[][] = []
      if ((gi.productionAccessDate || '').trim()) {
        rows.push(['Production access date', gi.productionAccessDate])
      }
      if ((gi.goLiveDate || '').trim()) {
        rows.push([
          'Expected go-live date',
          `${gi.goLiveDate}${gi.isHardGoLiveDate ? ' (Hard date)' : ''}`,
        ])
      }
      ensureSpace(24 + rows.length * 22)
      autoTable(doc, {
        startY: cursorY,
        margin: { top: 80 },
        head: [['Key date', 'Value']],
        body: rows,
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, valign: 'top', overflow: 'linebreak' },
        headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: brandLightRgb },
        columnStyles: {
          0: { cellWidth: 180 },
          1: { cellWidth: contentWidth - 180 },
        },
        didDrawPage: () => drawLogo(),
      })
      // @ts-expect-error - autotable plugin
      cursorY = doc.lastAutoTable.finalY + 16
    }
  }

  // ── Project Phases (from General, with SOW dates) ──
  const phaseRows = gi.phases
    .map((text, idx) => ({ idx, text: (text || '').trim(), sow: sow.phases[idx] }))
    .filter((p) => p.text.length > 0)
  if (phaseRows.length > 0) {
    writeSectionHeading('Project Phases')
    ensureSpace(24 + phaseRows.length * 40)
    autoTable(doc, {
      startY: cursorY,
      margin: { top: 80 },
      head: [['Phase', 'Scope', 'Start', 'End']],
      body: phaseRows.map((p) => [
        `Phase ${p.idx + 1}`,
        p.text,
        p.sow?.startDate || '—',
        p.sow?.endDate || '—',
      ]),
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, valign: 'top', overflow: 'linebreak' },
      headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: brandLightRgb },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: contentWidth - 70 - 80 - 80 },
        2: { cellWidth: 80 },
        3: { cellWidth: 80 },
      },
      didDrawPage: () => drawLogo(),
    })
    // @ts-expect-error - autotable plugin
    cursorY = doc.lastAutoTable.finalY + 16
  }

  // ── SOW Variation Note ──
  if ((gi.sowVariationNote || '').trim()) {
    writeSectionHeading('SOW Variation Note')
    writeParagraph(gi.sowVariationNote, 10)
  }

  // ── Key Milestones ──
  if (sow.milestones.length > 0) {
    writeSectionHeading('Key Milestones')
    ensureSpace(24 + sow.milestones.length * 22)
    autoTable(doc, {
      startY: cursorY,
      margin: { top: 80 },
      head: [['Milestone', 'Date', 'Description']],
      body: sow.milestones.map((m) => [m.name || '—', m.date || '—', m.description || '']),
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, valign: 'top', overflow: 'linebreak' },
      headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: brandLightRgb },
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 90 },
        2: { cellWidth: contentWidth - 150 - 90 },
      },
      didDrawPage: () => drawLogo(),
    })
    // @ts-expect-error - autotable plugin
    cursorY = doc.lastAutoTable.finalY + 16
  }

  // ── Integration Sequence Diagram(s) ──
  if ((sow.sequenceDiagramDsl || '').trim()) {
    const DIAGRAM_BREAK = '===DIAGRAM BREAK==='
    const diagramDsls = sow.sequenceDiagramDsl.split(DIAGRAM_BREAK).map((s: string) => s.trim()).filter(Boolean)
    const validFlows = diagramDsls
      .map((dsl: string) => parseDsl(scope.customerName || 'scope', dsl).flow)
      .filter((f) => f.participants.length > 0)
    if (validFlows.length > 0) {
      writeSectionHeading('Integration Sequence Diagram')
      for (let di = 0; di < validFlows.length; di++) {
        const seqFlow = validFlows[di]
        if (validFlows.length > 1 && seqFlow.title) {
          // Sub-heading for each diagram when there are multiple
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(10)
          doc.setTextColor(60, 60, 60)
          ensureSpace(20)
          doc.text(`${di + 1}. ${seqFlow.title}`, leftMargin, cursorY)
          cursorY += 14
        }
        const seqImg = await sequenceDiagramToPng(seqFlow, contentWidth)
        if (seqImg) {
          ensureSpace(seqImg.height + 16)
          doc.addImage(seqImg.dataUrl, 'JPEG', leftMargin, cursorY, seqImg.width, seqImg.height)
          cursorY += seqImg.height + (di < validFlows.length - 1 ? 24 : 16)
        }
      }
    }
  }

  // ── RACI Chart ──
  if (sow.raciColumns.length > 0 && sow.raciEntries.length > 0) {
    writeSectionHeading('RACI Chart')
    const raciLabels: Record<string, string> = { R: 'Responsible', A: 'Accountable', C: 'Consulted', I: 'Informed' }
    const raciCellColors: Record<string, [number, number, number]> = {
      R: [219, 234, 254], // blue-100
      A: [220, 252, 231], // green-100
      C: [254, 249, 195], // yellow-100
      I: [243, 244, 246], // gray-100
    }
    const raciTextColors: Record<string, [number, number, number]> = {
      R: [30, 64, 175],   // blue-800
      A: [22, 101, 52],   // green-800
      C: [133, 77, 14],   // yellow-800
      I: [75, 85, 99],    // gray-600
    }
    // Store raw RACI values per [row][col] for color lookup (col index offset by 1 for Task column)
    const raciRaw = sow.raciEntries.map((e) =>
      sow.raciColumns.map((c) => e.assignments?.[c.id] || '')
    )
    const raciHead = ['Task', ...sow.raciColumns.map((c) => c.name)]
    const raciBody = sow.raciEntries.map((e) => [
      e.task || '—',
      ...sow.raciColumns.map((c) => {
        const v = e.assignments?.[c.id]
        return v ? (raciLabels[v] || v) : '—'
      }),
    ])
    ensureSpace(24 + raciBody.length * 20)
    autoTable(doc, {
      startY: cursorY,
      margin: { top: 80 },
      head: [raciHead],
      body: raciBody,
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, valign: 'top', overflow: 'linebreak' },
      headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255] },
      didDrawPage: () => drawLogo(),
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index > 0) {
          const raw = raciRaw[data.row.index]?.[data.column.index - 1]
          if (raw && raciCellColors[raw]) {
            data.cell.styles.fillColor = raciCellColors[raw]
            data.cell.styles.textColor = raciTextColors[raw]
            data.cell.styles.fontStyle = 'bold'
          }
        }
      },
    })
    // @ts-expect-error - autotable plugin
    cursorY = doc.lastAutoTable.finalY + 16
  }

  // ── Gantt Chart ──
  const ganttTasks = sow.ganttTasks?.filter((t) => t.taskName || t.startDate || t.endDate) || []
  if (ganttTasks.length > 0) {
    writeSectionHeading('Gantt Chart')

    // Task table
    ensureSpace(24 + ganttTasks.length * 22)
    autoTable(doc, {
      startY: cursorY,
      margin: { top: 80 },
      head: [['Task', 'Responsible', 'Party', 'Start', 'End']],
      body: ganttTasks.map((t) => [
        t.taskName || '—',
        t.responsiblePerson || '—',
        t.party === 'frankieone' ? 'FrankieOne' : t.party === 'client' ? 'Client' : '—',
        t.startDate || '—',
        t.endDate || '—',
      ]),
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 6, valign: 'top', overflow: 'linebreak' },
      headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: brandLightRgb },
      columnStyles: {
        0: { cellWidth: contentWidth - 100 - 80 - 80 - 80 },
        1: { cellWidth: 100 },
        2: { cellWidth: 80 },
        3: { cellWidth: 80 },
        4: { cellWidth: 80 },
      },
      didDrawPage: () => drawLogo(),
    })
    // @ts-expect-error - autotable plugin
    cursorY = doc.lastAutoTable.finalY + 16

    // Visual timeline bars
    const datedTasks = ganttTasks.filter((t) => t.startDate && t.endDate)
    if (datedTasks.length > 0) {
      const dates = datedTasks.flatMap((t) => [new Date(t.startDate).getTime(), new Date(t.endDate).getTime()])
      const minDate = Math.min(...dates)
      const maxDate = Math.max(...dates)
      const range = maxDate - minDate || 1

      const labelColWidth = 150
      const barAreaX = leftMargin + labelColWidth + 6
      const barAreaWidth = contentWidth - labelColWidth - 6
      const rowHeight = 18
      const barHeight = 12

      // Month labels along the top
      const startMonth = new Date(minDate)
      startMonth.setDate(1)
      const monthCursor = new Date(startMonth)

      const totalHeight = 20 + datedTasks.length * rowHeight + 24
      ensureSpace(totalHeight)

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7)
      doc.setTextColor(120, 120, 120)
      while (monthCursor.getTime() <= maxDate) {
        const pct = (monthCursor.getTime() - minDate) / range
        if (pct >= 0 && pct <= 1) {
          const x = barAreaX + pct * barAreaWidth
          doc.text(
            monthCursor.toLocaleString('default', { month: 'short', year: '2-digit' }),
            x,
            cursorY
          )
        }
        monthCursor.setMonth(monthCursor.getMonth() + 1)
      }
      cursorY += 10

      // Task bars
      const frankieBlue: [number, number, number] = [59, 130, 246]
      const clientAmber: [number, number, number] = [251, 191, 36]

      for (const task of datedTasks) {
        const start = new Date(task.startDate).getTime()
        const end = new Date(task.endDate).getTime()
        const leftPct = (start - minDate) / range
        const widthPct = Math.max((end - start) / range, 0.02)

        // Task label
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.setTextColor(80, 80, 80)
        const label = task.taskName.length > 28 ? task.taskName.slice(0, 26) + '...' : task.taskName
        doc.text(label, leftMargin, cursorY + barHeight - 2)

        // Background track
        doc.setFillColor(240, 240, 240)
        doc.roundedRect(barAreaX, cursorY, barAreaWidth, barHeight, 2, 2, 'F')

        // Colored bar
        const color = task.party === 'client' ? clientAmber : frankieBlue
        doc.setFillColor(...color)
        const barX = barAreaX + leftPct * barAreaWidth
        const barW = Math.max(widthPct * barAreaWidth, 4)
        doc.roundedRect(barX, cursorY, barW, barHeight, 2, 2, 'F')

        cursorY += rowHeight
      }

      // Legend
      cursorY += 4
      doc.setFontSize(7)
      doc.setTextColor(80, 80, 80)
      doc.setFillColor(...frankieBlue)
      doc.roundedRect(leftMargin, cursorY - 6, 8, 8, 1, 1, 'F')
      doc.text('FrankieOne', leftMargin + 12, cursorY)
      doc.setFillColor(...clientAmber)
      doc.roundedRect(leftMargin + 80, cursorY - 6, 8, 8, 1, 1, 'F')
      doc.text('Client', leftMargin + 92, cursorY)
      cursorY += 16
    }
  }

  // ── Key Assumptions ──
  {
    const V2_CUTOFF = '2026-08-31'
    const v2Feats: string[] = []
    if (scope.kybConfig?.internationalEnabled === 'yes') v2Feats.push('International KYB')
    if (scope.kybConfig?.trustAnalyserEnabled === 'yes') v2Feats.push('Trust Analyser')
    if (scope.kybConfig?.onboardingFormEnabled === 'yes') v2Feats.push('Business Onboarding Form')
    if (scope.kybConfig?.amlEntityScreening === 'yes') v2Feats.push('Business AML')
    const goLiveBeforeCutoff = scope.generalInfo?.goLiveDate && scope.generalInfo.goLiveDate < V2_CUTOFF
    const v2Note = v2Feats.length > 0 && goLiveBeforeCutoff
      ? `The following features are currently in development and will not be available for implementation as part of the initial go-live: ${v2Feats.join(', ')}. These features will be delivered in a subsequent phase once generally available.`
      : null

    const combined = [sow.keyAssumptions?.trim(), v2Note].filter(Boolean).join('\n\n')
    if (combined) {
      writeSectionHeading('Key Assumptions')
      writeParagraph(combined, 10)
    }
  }

  // ── Additional Notes ──
  if ((sow.notes || '').trim()) {
    writeSectionHeading('Additional Notes')
    writeParagraph(sow.notes, 10)
  }

  // ── Scope Summary (full) on a new page ──
  doc.addPage()
  drawLogo()
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text('Scope Configuration Summary', leftMargin, 50)
  doc.setTextColor(0, 0, 0)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(
    'Full scope configuration underpinning this Statement of Work.',
    leftMargin,
    70
  )

  // Legend
  doc.setFontSize(9)
  const legendY = 110
  doc.setFillColor(...BRAND.warning)
  doc.rect(leftMargin, legendY - 8, 10, 10, 'F')
  doc.setTextColor(0, 0, 0)
  doc.text(
    'Highlighted items indicate a decision is still required (empty / unknown / tbd).',
    leftMargin + 16,
    legendY
  )

  const summaryStartY = legendY + 30
  const pendingCount = buildExportRows(scope).filter(isVisiblyPending).length
  doc.setFontSize(11)
  doc.text(`Customer: ${scope.customerName || '—'}`, leftMargin, summaryStartY)
  doc.text(
    `Status: ${titleCaseFromSnake(gi.scopeStatus || 'draft')}${scope.finalized ? ' (Finalised)' : ''}`,
    leftMargin,
    summaryStartY + 16
  )
  doc.text(`Pending decisions: ${pendingCount}`, leftMargin, summaryStartY + 32)

  renderScopeSummaryTables(doc, scope, drawLogo, summaryStartY + 60, pageWidth, pageHeight)

  // ── Signature Page ──
  // Customer attests that the captured configuration is an accurate
  // representation of what FrankieOne will deliver.
  doc.addPage()
  drawLogo()
  cursorY = 60

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text('Acknowledgement & Signature', leftMargin, cursorY)
  doc.setTextColor(0, 0, 0)
  cursorY += 26

  const acknowledgement =
    `By signing below, ${scope.customerName || 'the Client'} confirms that ` +
    `FrankieOne has accurately captured their configuration requirements ` +
    `and that this document is a true and accurate representation of the ` +
    `services and configuration FrankieOne will deliver under this Statement of Work.`
  writeParagraph(acknowledgement, 10)
  cursorY += 10

  // Signature blocks: Client (left) + FrankieOne (right)
  const blockGap = 30
  const blockWidth = (contentWidth - blockGap) / 2
  const signatureLineY = (baseY: number) => baseY + 50

  const drawSignatureBlock = (heading: string, x: number, y: number, nameLabel: string, fixedName?: string) => {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.text(heading, x, y)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text('Signature', x, y + 14)
    doc.setTextColor(0, 0, 0)

    // Signature line
    doc.setDrawColor(60, 60, 60)
    doc.setLineWidth(0.6)
    doc.line(x, signatureLineY(y), x + blockWidth, signatureLineY(y))

    let lineY = signatureLineY(y) + 22

    // Name
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text(nameLabel, x, lineY)
    doc.setTextColor(0, 0, 0)
    if (fixedName) {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(fixedName, x, lineY + 14)
      doc.setFont('helvetica', 'normal')
    } else {
      doc.line(x, lineY + 16, x + blockWidth, lineY + 16)
    }
    lineY += 32

    // Title
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text('Title', x, lineY)
    doc.setTextColor(0, 0, 0)
    doc.line(x, lineY + 16, x + blockWidth, lineY + 16)
    lineY += 32

    // Date
    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text('Date', x, lineY)
    doc.setTextColor(0, 0, 0)
    doc.line(x, lineY + 16, x + blockWidth, lineY + 16)
  }

  ensureSpace(220)
  const blocksY = cursorY
  drawSignatureBlock(
    'For and on behalf of the Client',
    leftMargin,
    blocksY,
    'Name',
    scope.customerName || undefined
  )
  drawSignatureBlock(
    'For and on behalf of FrankieOne',
    leftMargin + blockWidth + blockGap,
    blocksY,
    'Name'
  )

  const safeName = (scope.customerName || 'scope').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 50)
  doc.save(`${safeName}_SOW.pdf`)
}

// =====================================================
// HANDOVER TO IMPLEMENTATIONS PDF
// =====================================================

export const exportHandoverToPdf = async (scope: ScopeData, tasks: PresalesTask[], dealId?: number) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })

  const brandPrimaryRgb = hexToRgb(BRAND.primary)
  const brandLightRgb = hexToRgb(BRAND.light)
  const logo = await getImageDataUrlFromPublicWebp('/FrankieOne-MainLogo-2.webp')

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const drawLogo = () => {
    if (!logo) return
    const margin = 40
    const maxW = 95
    const maxH = 22
    const scale = Math.min(maxW / logo.width, maxH / logo.height)
    const w = Math.max(1, logo.width * scale)
    const h = Math.max(1, logo.height * scale)
    doc.addImage(logo.dataUrl, 'PNG', pageWidth - margin - w, 30, w, h)
  }

  let cursorY = 0

  const ensureSpace = (requiredHeight: number) => {
    const bottomMargin = 60
    if (cursorY + requiredHeight <= pageHeight - bottomMargin) return
    doc.addPage()
    drawLogo()
    cursorY = 90
  }

  // ========== Title Page ==========
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text('Handover to Implementations', 40, 50)
  doc.setTextColor(0, 0, 0)
  drawLogo()

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(
    'This document summarises the scope configuration and outstanding tasks for implementation handover.',
    40,
    75,
  )

  const topBlockY = 105
  doc.setFontSize(11)
  doc.text(`Customer: ${scope.customerName || '—'}`, 40, topBlockY)
  doc.text(
    `Status: ${titleCaseFromSnake(scope.generalInfo.scopeStatus || 'draft')}${scope.finalized ? ' (Finalised)' : ''}`,
    40,
    topBlockY + 16,
  )
  doc.text(`Go-live date: ${scope.generalInfo.goLiveDate || '—'}${scope.generalInfo.isHardGoLiveDate ? ' (Hard deadline)' : ''}`, 40, topBlockY + 32)
  doc.text(`Production access date: ${scope.generalInfo.productionAccessDate || '—'}`, 40, topBlockY + 48)
  doc.text(`Generated: ${new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}`, 40, topBlockY + 64)

  const outstandingTasks = tasks.filter((t) => t.status !== 'completed')
  doc.text(`Outstanding tasks: ${outstandingTasks.length}`, 40, topBlockY + 80)
  doc.text(`Custom work items: ${scope.generalInfo.customWorks?.length || 0}`, 40, topBlockY + 96)

  // ========== Link to Full Scope ==========
  cursorY = topBlockY + 120

  if (dealId) {
    const scopeUrl = `https://dealhub.pmgoncalves0.workers.dev/scope?session=${dealId}`
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(...brandPrimaryRgb)
    doc.textWithLink('View full scope configuration in DealHub', 40, cursorY, { url: scopeUrl })
    doc.setTextColor(0, 0, 0)
    cursorY += 24
  }

  // ========== TLDR Scope Summary ==========
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text('Scope Summary', 40, cursorY)
  doc.setTextColor(0, 0, 0)
  cursorY += 16

  const gi = scope.generalInfo
  const kyc = scope.kycConfig
  const aml = scope.amlConfig
  const idv = scope.biometricsConfig
  const kyb = scope.kybConfig

  // Determine which products are in scope
  const products: string[] = []
  if (kyc.kycRequired === 'yes') products.push('KYC')
  if (aml.level && aml.level !== 'na' && aml.level !== 'unknown') products.push('AML')
  if (kyb.enabled === 'yes') products.push('KYB')
  if (idv.enabled === 'yes') products.push('IDV')

  // Build overview rows
  const tldrRows: [string, string][] = [
    ['Client Use Case', gi.clientUseCase || '—'],
    ['API Version', gi.platformVersion === 'v1' ? 'V1' : gi.platformVersion === 'v2' ? 'V2' : '—'],
    ['Integration Type', titleCaseFromSnake(gi.integrationType || 'unknown')],
    ['Products in Scope', products.length > 0 ? products.join(', ') : '—'],
  ]

  // KYC details
  if (kyc.kycRequired === 'yes') {
    const workflowNames = scope.workflowsConfig
      .map((w) => w.name)
      .filter(Boolean)
    tldrRows.push(['', ''])  // spacer
    tldrRows.push(['KYC — Workflows', workflowNames.length > 0 ? workflowNames.join(', ') : '—'])

    const countries: string[] = []
    if (kyc.documentTypesAu?.length > 0) countries.push('Australia')
    if (kyc.acceptNzCustomers === 'yes' || (kyc.documentTypesNz?.length ?? 0) > 0) countries.push('New Zealand')
    if (kyc.internationalCountries?.length > 0) countries.push(...kyc.internationalCountries)
    tldrRows.push(['KYC — Countries', countries.length > 0 ? countries.join(', ') : '—'])

    const cascadeSources = (kyc.selectedDataSources || [])
      .map((ds) => {
        const match = WORKFLOW_CASCADE_SOURCES.find((s) => s.id === ds.sourceId)
        return match?.label || titleCaseFromSnake(ds.sourceId)
      })
    tldrRows.push(['KYC — Data Sources', cascadeSources.length > 0 ? cascadeSources.join(', ') : '—'])
  }

  // IDV details
  if (idv.enabled === 'yes') {
    const idvVendors = idv.flows
      ?.map((f) => f.vendor)
      .filter((v) => v && v !== 'unknown' && v !== 'no_vendor')
      .map((v) => titleCaseFromSnake(v))
    const uniqueVendors = [...new Set(idvVendors || [])]
    const vendorFallback = idv.vendor && idv.vendor !== 'unknown' ? titleCaseFromSnake(idv.vendor) : null

    tldrRows.push(['', ''])
    tldrRows.push(['IDV — Vendor', uniqueVendors.length > 0 ? uniqueVendors.join(', ') : vendorFallback || '—'])

    // Double ID summary for Daon flows
    const daonFlowsWithDoubleId = (idv.flows || []).filter((f) => f.vendor === 'daon' && f.doubleIdEnabled === 'yes')
    if (daonFlowsWithDoubleId.length > 0) {
      const flowNames = daonFlowsWithDoubleId.map((f) => f.name || f.id || 'Unnamed').join(', ')
      tldrRows.push(['IDV — Double ID', `Yes (${flowNames})`])
    }
  }

  // AML details
  if (aml.level && aml.level !== 'na' && aml.level !== 'unknown') {
    const amlVendor = aml.vendor && aml.vendor !== 'unknown' ? titleCaseFromSnake(aml.vendor) : '—'

    const screeningTypes: string[] = []
    if (aml.pepScreeningConfig?.enabled === 'yes') screeningTypes.push('PEPs')
    if (aml.sanctionsScreeningConfig?.enabled === 'yes') screeningTypes.push('Sanctions')
    if (aml.adverseMediaScreeningConfig?.enabled === 'yes') screeningTypes.push('Adverse Media')
    if (aml.additionalWatchlistsConfig?.enabled === 'yes') screeningTypes.push('Watchlists')

    tldrRows.push(['', ''])
    tldrRows.push(['AML — Vendor', amlVendor])
    tldrRows.push(['AML — Screening', screeningTypes.length > 0 ? screeningTypes.join(', ') : '—'])
    tldrRows.push(['AML — Monitoring', titleCaseFromSnake(aml.monitoringType || 'unknown')])
  }

  // KYB details
  if (kyb.enabled === 'yes') {
    tldrRows.push(['', ''])
    tldrRows.push(['KYB', 'In scope'])
  }

  autoTable(doc, {
    startY: cursorY,
    margin: { top: 80, left: 40, right: 40 },
    body: tldrRows.map(([label, value]) => [label, value]),
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 5, valign: 'top', overflow: 'linebreak', halign: 'left' },
    alternateRowStyles: { fillColor: brandLightRgb },
    columnStyles: {
      0: { cellWidth: 150, fontStyle: 'bold' },
      1: { cellWidth: pageWidth - 80 - 150 },
    },
    didDrawPage: () => {
      drawLogo()
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const row = tldrRows[data.row.index]
        // Style section header rows (KYC, IDV, AML, KYB prefixed labels)
        if (row && row[0] === '' && row[1] === '') {
          data.cell.styles.fillColor = [255, 255, 255]
          data.cell.styles.cellPadding = { top: 2, bottom: 2, left: 5, right: 5 }
        }
      }
    },
  })

  // @ts-expect-error - autotable plugin
  cursorY = doc.lastAutoTable.finalY + 16

  // ========== Outstanding Tasks Section ==========
  doc.addPage()
  drawLogo()
  cursorY = 50

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text('Outstanding Tasks', 40, cursorY)
  doc.setTextColor(0, 0, 0)
  cursorY += 20

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text(`${outstandingTasks.length} task${outstandingTasks.length !== 1 ? 's' : ''} remaining`, 40, cursorY)
  cursorY += 16

  if (outstandingTasks.length > 0) {
    // Sort: critical path first, then by due date
    const sorted = [...outstandingTasks].sort((a, b) => {
      if (a.is_critical_path && !b.is_critical_path) return -1
      if (!a.is_critical_path && b.is_critical_path) return 1
      if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date)
      if (a.due_date) return -1
      if (b.due_date) return 1
      return 0
    })

    const today = new Date().toISOString().split('T')[0]

    autoTable(doc, {
      startY: cursorY,
      margin: { top: 80, left: 40, right: 40 },
      head: [['Description', 'Status', 'Assigned To', 'Due Date', 'Key Person', 'Jira', 'CP', 'Status Update']],
      body: sorted.map((t) => [
        t.description || '—',
        titleCaseFromSnake(t.status),
        t.assigned_to || '—',
        t.due_date || '—',
        t.key_person || '—',
        t.jira_ticket || '—',
        t.is_critical_path ? '✓' : '—',
        t.status_update || '—',
      ]),
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 4, valign: 'top', overflow: 'linebreak', halign: 'left' },
      headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255], fontSize: 7 },
      alternateRowStyles: { fillColor: brandLightRgb },
      columnStyles: {
        0: { cellWidth: 130 },
        1: { cellWidth: 45 },
        2: { cellWidth: 60 },
        3: { cellWidth: 55 },
        4: { cellWidth: 55 },
        5: { cellWidth: 55 },
        6: { cellWidth: 22 },
        7: { cellWidth: pageWidth - 80 - 130 - 45 - 60 - 55 - 55 - 55 - 22 },
      },
      didDrawPage: () => {
        drawLogo()
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const task = sorted[data.row.index]
          // Highlight critical path rows
          if (task?.is_critical_path) {
            data.cell.styles.fillColor = [255, 247, 237]
            if (data.column.index === 0) data.cell.styles.fontStyle = 'bold'
          }
          // Highlight overdue tasks
          if (data.column.index === 3 && task?.due_date && task.due_date < today) {
            data.cell.styles.textColor = [220, 38, 38]
            data.cell.styles.fontStyle = 'bold'
          }
          // Style Jira column as link
          if (data.column.index === 5 && task?.jira_ticket) {
            data.cell.styles.textColor = [0, 105, 255]
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 5) {
          const task = sorted[data.row.index]
          if (task?.jira_ticket) {
            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
              url: toJiraUrl(task.jira_ticket),
            })
          }
        }
      },
    })

    // @ts-expect-error - autotable plugin
    cursorY = doc.lastAutoTable.finalY + 16
  } else {
    doc.setFontSize(10)
    doc.text('No outstanding tasks.', 40, cursorY)
    cursorY += 20
  }

  // ========== Custom Work Items Section ==========
  const customWorks = scope.generalInfo.customWorks || []

  if (customWorks.length > 0) {
    ensureSpace(60)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.setTextColor(...brandPrimaryRgb)
    doc.text('Custom Work Items', 40, cursorY)
    doc.setTextColor(0, 0, 0)
    cursorY += 20

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.text(`${customWorks.length} custom work item${customWorks.length !== 1 ? 's' : ''}`, 40, cursorY)
    cursorY += 16

    autoTable(doc, {
      startY: cursorY,
      margin: { top: 80, left: 40, right: 40 },
      head: [['Name', 'Category', 'Effort', 'Due Date', 'Status', 'PM Approval', 'Jira']],
      body: customWorks.map((cw) => [
        cw.name || '—',
        titleCaseFromSnake(cw.category || ''),
        cw.effortDays != null ? `${cw.effortDays}d` : '—',
        cw.dueDate || '—',
        titleCaseFromSnake(cw.lifecycleStatus || ''),
        titleCaseFromSnake(cw.pmApprovalStatus || ''),
        cw.jiraTickets?.map((j) => `${j.ticket} (${j.status})`).join(', ') || '—',
      ]),
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 5, valign: 'top', overflow: 'linebreak', halign: 'left' },
      headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255], fontSize: 8 },
      alternateRowStyles: { fillColor: brandLightRgb },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 65 },
        2: { cellWidth: 40 },
        3: { cellWidth: 60 },
        4: { cellWidth: 65 },
        5: { cellWidth: 70 },
        6: { cellWidth: pageWidth - 80 - 120 - 65 - 40 - 60 - 65 - 70 },
      },
      didDrawPage: () => {
        drawLogo()
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const cw = customWorks[data.row.index]
          // Highlight items needing PM approval
          if (cw?.pmApprovalStatus === 'pending' || cw?.pmApprovalStatus === 'not_requested') {
            if (data.column.index === 5) {
              data.cell.styles.textColor = BRAND.warning
              data.cell.styles.fillColor = [255, 247, 237]
            }
          }
          // Style Jira column as link
          if (data.column.index === 6 && cw?.jiraTickets?.length) {
            data.cell.styles.textColor = [0, 105, 255]
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 6) {
          const cw = customWorks[data.row.index]
          const firstTicket = cw?.jiraTickets?.[0]?.ticket
          if (firstTicket) {
            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
              url: toJiraUrl(firstTicket),
            })
          }
        }
      },
    })

    // @ts-expect-error - autotable plugin
    cursorY = doc.lastAutoTable.finalY + 16
  }

  // ========== Custom Work Detail (descriptions) ==========
  const detailedWorks = customWorks.filter((cw) => cw.description && cw.description.trim())
  if (detailedWorks.length > 0) {
    ensureSpace(40)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.setTextColor(0, 0, 0)
    doc.text('Custom Work Descriptions', 40, cursorY)
    cursorY += 16

    detailedWorks.forEach((cw) => {
      const nameLines = doc.splitTextToSize(cw.name || '—', pageWidth - 80)
      const descLines = doc.splitTextToSize(cw.description, pageWidth - 92)

      const blockHeight = nameLines.length * 12 + descLines.length * 11 + 20
      ensureSpace(blockHeight)

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(nameLines, 40, cursorY)
      cursorY += nameLines.length * 12 + 4

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.setTextColor(...BRAND.slate)
      doc.text(descLines, 46, cursorY)
      cursorY += descLines.length * 11 + 12
      doc.setTextColor(0, 0, 0)
    })
  }

  // ========== Presales Actions Section ==========
  const presalesActions = scope.generalInfo.presalesActions || []
  const actionRows: [string, string, string, string][] = PRESALES_ACTIONS.map((def) => {
    const action = presalesActions.find((a) => a.id === def.id)
    const status = action?.notRequired ? 'N/A' : action?.completed ? 'Done' : 'Pending'
    const date = action?.completedAt && !action?.notRequired ? action.completedAt : '—'
    const notes = action?.notRequired ? '' : action?.notes || ''
    return [def.label, status, date, notes]
  })

  ensureSpace(60)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(...brandPrimaryRgb)
  doc.text('Presales Actions', 40, cursorY)
  doc.setTextColor(0, 0, 0)
  cursorY += 20

  autoTable(doc, {
    startY: cursorY,
    margin: { top: 80, left: 40, right: 40 },
    head: [['Action', 'Status', 'Date', 'Notes']],
    body: actionRows,
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 5, valign: 'top', overflow: 'linebreak', halign: 'left' },
    headStyles: { fillColor: brandPrimaryRgb, textColor: [255, 255, 255], fontSize: 8 },
    alternateRowStyles: { fillColor: brandLightRgb },
    columnStyles: {
      0: { cellWidth: 160 },
      1: { cellWidth: 50 },
      2: { cellWidth: 70 },
      3: { cellWidth: pageWidth - 80 - 160 - 50 - 70 },
    },
    didDrawPage: () => { drawLogo() },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const val = data.cell.raw as string
        if (val === 'Done') data.cell.styles.textColor = [22, 163, 74]
        else if (val === 'N/A') data.cell.styles.textColor = [107, 114, 128]
        else data.cell.styles.textColor = [234, 88, 12]
      }
    },
  })

  // @ts-expect-error - autotable plugin
  cursorY = doc.lastAutoTable.finalY + 16

  const safeName = (scope.customerName || 'scope').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 50)
  doc.save(`${safeName}_handover.pdf`)
}

/** Sections whose pending rows are hidden from the PDF tables (see renderScopeSummaryTables). */
const COLLAPSED_SECTIONS = new Set(['Advanced', 'Custom Work'])

const isVisiblyPending = (r: ExportRow): boolean => {
  const pending = r.skipPendingCheck ? Boolean(r.pending) : (Boolean(r.pending) || isDecisionRequiredValue(r.value))
  if (!pending) return false
  // Advanced / Custom Work pending rows are filtered out of the PDF — don't count them.
  if (COLLAPSED_SECTIONS.has(r.section)) return false
  return true
}

const buildPendingDecisionsList = (scope: ScopeData): string[] => {
  return buildExportRows(scope)
    .filter(isVisiblyPending)
    .map((r) => `${r.section}: ${r.field}`)
}

const buildCompactSectionedText = (scope: ScopeData): { sections: Array<{ title: string; lines: string[] }>; text: string } => {
  const rows = buildExportRows(scope, { includeNonConfigSections: true })

  const grouped = rows.reduce<Record<string, ExportRow[]>>((acc, r) => {
    acc[r.section] ||= []
    acc[r.section].push(r)
    return acc
  }, {})

  const sections = Object.entries(grouped).map(([title, sectionRows]) => {
    const lines = sectionRows.map((r) => {
      const v = r.value && r.value.trim() ? r.value : '—'
      return `${r.field}: ${v}`
    })
    return { title, lines }
  })

  const pending = buildPendingDecisionsList(scope)

  const text = [
    'Scope Configuration Summary',
    '',
    `Customer: ${scope.customerName || '—'}`,
    `Status: ${scope.generalInfo.scopeStatus || 'draft'}${scope.finalized ? ' (Finalised)' : ''}`,
    `Pending decisions: ${pending.length}`,
    ...(pending.length
      ? ['Pending decisions list:', ...pending.map((p) => `- ${p}`)]
      : ['Pending decisions list:', '- None']),
    '',
    ...sections.flatMap((s) => [s.title, ...s.lines.map((l) => `- ${l}`), '']),
  ].join('\n')

  return { sections, text }
}

/** Builds the Jira ticket JSON payload and returns it as a string (does not download). */
export const buildJiraJsonPayload = (scope: ScopeData): string | null => {
  const gi = scope.generalInfo
  const cascadeDef = getCascadeDefinition(scope)

  const uatIds = [gi.parentUatId, gi.childUatId].filter((x) => x && x.trim())
  if (uatIds.length === 0) {
    return null
  }

  const { sections, text } = buildCompactSectionedText(scope)

  const payload = {
    type: 'uat_configuration_request',
    createdAt: new Date().toISOString(),
    customer: {
      name: scope.customerName,
      ids: {
        parentUatId: gi.parentUatId || null,
        childUatId: gi.childUatId || null,
        parentProdId: gi.parentProdId || null,
        childProdId: gi.childProdId || null,
      },
    },
    summary: {
      finalized: scope.finalized,
      scopeStatus: gi.scopeStatus,
      scopeStatusDates: gi.scopeStatusDates,
      cascadeDefined: cascadeDef.defined,
      pendingDecisions: buildPendingDecisionsList(scope),
    },
    sections,
    description: text,
  }

  return JSON.stringify(payload, null, 2)
}

export const exportScopeToJiraJson = (scope: ScopeData) => {
  const gi = scope.generalInfo
  const cascadeDef = getCascadeDefinition(scope)

  const uatIds = [gi.parentUatId, gi.childUatId].filter((x) => x && x.trim())
  if (uatIds.length === 0) {
    showToast('Please set the customer ID (Parent UAT ID and/or Child UAT ID) before exporting a UAT request.', 'warning')
    return
  }

  const { sections, text } = buildCompactSectionedText(scope)

  const payload = {
    type: 'uat_configuration_request',
    createdAt: new Date().toISOString(),
    customer: {
      name: scope.customerName,
      ids: {
        parentUatId: gi.parentUatId || null,
        childUatId: gi.childUatId || null,
        parentProdId: gi.parentProdId || null,
        childProdId: gi.childProdId || null,
      },
    },
    summary: {
      finalized: scope.finalized,
      scopeStatus: gi.scopeStatus,
      scopeStatusDates: gi.scopeStatusDates,
      cascadeDefined: cascadeDef.defined,
      pendingDecisions: buildPendingDecisionsList(scope),
    },
    sections,
    description: text,
  }

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  const safeName = (scope.customerName || 'uat_request').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 50)
  downloadBlob(blob, `${safeName}_uat_request.json`)
}

// ─── Jira AI Prompt Context ─────────────────────────────────────────

/**
 * Compute a due date that is 5 working days from today, skipping weekends.
 */
function getFiveWorkingDaysFromNow(): string {
  const d = new Date()
  let added = 0
  while (added < 5) {
    d.setDate(d.getDate() + 1)
    const dow = d.getDay()
    if (dow !== 0 && dow !== 6) added++
  }
  return d.toISOString().slice(0, 10) // YYYY-MM-DD
}

/**
 * Build a structured context object from scope data for Jira AI ticket generation.
 * This extracts only the fields the prompt template needs.
 */
export function buildJiraPromptContext(scope: ScopeData) {
  const gi = scope.generalInfo
  const aml = scope.amlConfig
  const bio = scope.biometricsConfig
  const kyb = scope.kybConfig

  // Map frontend enum to human-readable label
  const frontendLabel = (() => {
    switch (bio.frontend) {
      case 'hosted_onesdk': return 'Hosted OneSDK'
      case 'embedded_onesdk': return 'Embedded OneSDK'
      case 'none': return 'No OneSDK'
      case 'other': return bio.otherFrontend || 'Other'
      default: return bio.frontend || 'Unknown'
    }
  })()

  return {
    customerName: scope.customerName,
    dueDate: getFiveWorkingDaysFromNow(),
    integrationType: gi.integrationType || 'unknown',
    platformVersion: gi.platformVersion || 'unknown',
    parentUatId: gi.parentUatId || '',
    childUatId: gi.childUatId || '',

    aml: {
      level: aml.level || 'unknown',
      vendor: aml.vendor || 'unknown',
      ongoingMonitoringEnabled: aml.ongoingMonitoringEnabled || 'unknown',
      monitoringType: aml.monitoringType || 'unknown',
      pepLevels: aml.pepLevels || [],
    },

    idv: {
      vendor: bio.vendor || 'unknown',
      frontend: frontendLabel,
    },

    kyb: {
      enabled: kyb.enabled || 'unknown',
      trustAnalyserEnabled: kyb.trustAnalyserEnabled || 'unknown',
      internationalEnabled: kyb.internationalEnabled || 'unknown',
      internationalCountryProducts: kyb.internationalCountryProducts || [],
      onboardingFormEnabled: kyb.onboardingFormEnabled || 'unknown',
    },

    recipes: scope.recipesConfig.map((r) => ({
      name: r.name,
      accountType: r.accountType,
      products: r.products,
      ruleset: r.ruleset || r.customRuleset || '',
    })),

    workflowsRequiredCount: scope.workflowsRequiredCount,
    workflowsNotes: scope.workflowsNotes || '',

    workflows: scope.workflowsConfig.map((wf) => {
      if (wf.kind === 'complex') {
        return {
          kind: 'complex' as const,
          name: wf.name,
          notes: wf.notes,
        }
      }
      // builder workflow
      return {
        kind: (wf.kind || 'builder') as string,
        name: wf.name,
        v1RecipeKey: wf.v1RecipeKey,
        steps: wf.steps?.map((s) => s.type) || [],
        notes: wf.notes,
      }
    }),
  }
}
